import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, useMap, Circle, ZoomControl, ScaleControl, Tooltip } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { postTenantPage, getLandingPageForTenant, createVisitBooking, resendOTP, verifyOTP, verifyMsg91Otp, sendMsg91Otp } from "../apis/services/pgApi";
import PhotoGalleryModal from "./PhotoGalleryModal";
import { useSearch } from "../context/searchcontext";

// Get landmarks based on locality
const getLandmarksForLocality = (locality) => {
  const landmarkMap = {
    'kamla nagar': [
      'Pg Near Kamla Nagar Market',
      'Pg Near Hans Raj College',
      'Pg Near Delhi University North Campus',
      'Pg Near Vishwavidyalaya Metro Station',
      'Pg Near Ramjas College',
      'Pg Near Miranda House',
      'Pg Near Kamla Nagar Police Station',
      'Pg Near Bara Hindu Rao Hospital',
      'Pg Near Kamla Nagar Park'
    ],
    'karol bagh': [
      'Pg Near Karol Bagh Market',
      'Pg Near Karol Bagh Metro Station',
      'Pg Near Jhandewalan Temple',
      'Pg Near Ganga Ram Hospital',
      'Pg Near Karol Bagh Police Station',
      'Pg Near Pusa Road',
      'Pg Near Karol Bagh Bus Terminal',
      'Pg Near Ajmal Khan Park'
    ],
    'mukherjee nagar': [
      'Pg Near Mukherjee Nagar Market',
      'Pg Near Delhi University North Campus',
      'Pg Near Vishwavidyalaya Metro Station',
      'Pg Near Mukherjee Nagar Coaching Hub',
      'Pg Near St. Stephen\'s College',
      'Pg Near Hindu College',
      'Pg Near Mukherjee Nagar Bus Stop',
      'Pg Near Ganga Ram Hospital'
    ],
    'rajinder nagar': [
      'Pg Near Rajinder Nagar Market',
      'Pg Near Rajendra Place Metro Station',
      'Pg Near Ganga Ram Hospital',
      'Pg Near Rajinder Nagar Police Station',
      'Pg Near Pusa Institute',
      'Pg Near Rajinder Nagar Park',
      'Pg Near Karol Bagh Market'
    ]
  };

  if (!locality) {
    console.log("❌ No locality provided for landmarks");
    return [];
  }

  const normalizedLocality = locality.toLowerCase().trim();
  console.log("🔍 Checking landmarks for:", normalizedLocality);
  
  // Check exact or partial match
  for (const [key, value] of Object.entries(landmarkMap)) {
    if (normalizedLocality.includes(key)) {
      console.log("✅ Found landmarks for:", key);
      return value;
    }
  }
  
  // Try partial word match
  const localityWords = normalizedLocality.split(/[,.\s]+/).filter(word => word.length > 2);
  for (const word of localityWords) {
    for (const [key, value] of Object.entries(landmarkMap)) {
      if (key.includes(word) || word.includes(key)) {
        console.log("✅ Partial match found for:", key);
        return value;
      }
    }
  }
  
  console.log("❌ No landmarks found for:", normalizedLocality);
  return [];
};

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// Custom PG Marker Icon
const createPGMarkerIcon = () => {
  return L.divIcon({
    className: "custom-pg-marker",
    html: `<div class="pg-marker">
            <div class="pg-marker-pulse"></div>
            <div class="pg-marker-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/>
                <circle cx="12" cy="10" r="3"/>
              </svg>
            </div>
            <div class="pg-marker-label">PG</div>
          </div>`,
    iconSize: [40, 48],
    iconAnchor: [20, 48],
    popupAnchor: [0, -48],
  });
};

// Category markers for nearby places
const getCategoryMarker = (category, placeName = "", tags = {}) => {
  let icon = "🚇";
  let label = category;
  let bg = "#10B981";

  if (category === "Transit") {
    // Better detection
    const name = placeName.toLowerCase();
    const isMetro = name.includes("metro") ||
      tags.railway === "station" && (tags.station === "subway" || tags.subway === "yes") ||
      name.includes("rohini east") ||
      name.includes("rohini west") ||
      name.includes("pitampura");

    const isBus = name.includes("bus") ||
      tags.highway === "bus_stop" ||
      tags.amenity === "bus_station" ||
      name.includes("depot") ||
      name.includes("chowk");

    if (isMetro && !isBus) {
      icon = "🚇";
      label = "Metro";
      bg = "#10B981";
    } else if (isBus) {
      icon = "🚌";
      label = "Bus";
      bg = "#3B82F6";
    } else {
      icon = "🚏";
      label = "Stop";
      bg = "#10B981";
    }
  } else if (category === "Food") {
    icon = "🍔";
    label = "Food";
    bg = "#F59E0B";
  } else if (category === "Shopping") {
    icon = "🛒";
    label = "Shop";
    bg = "#EF4444";
  } else if (category === "Utilities") {
    icon = "🏥";
    label = "Utility";
    bg = "#8B5CF6";
  }

  return L.divIcon({
    className: `category-marker category-${category.toLowerCase()}`,
    html: `<div class="category-marker-inner" style="background: ${bg}">
            <span class="category-icon">${icon}</span>
            <span class="category-label">${label}</span>
          </div>`,
    iconSize: [36, 36],
    iconAnchor: [18, 36],
    popupAnchor: [0, -18],
  });
};

const SECTION_TABS = ["About", "Renting Terms", "Packages", "Rules"];
const NEARBY_TABS = ["Utilities", "Transit", "Food", "Shopping"];

// Static fallback data for nearby places (based on locality)
const getStaticNearbyData = (locality) => {
  const defaultData = {
    Utilities: [
      { name: "Nearby Hospital", distance: "1.2 km" },
      { name: "Pharmacy", distance: "0.5 km" },
      { name: "Bank", distance: "0.8 km" },
      { name: "ATM", distance: "0.3 km" },
      { name: "Clinic", distance: "0.7 km" }
    ],
    Transit: [
      { name: "Metro Station", distance: "1.0 km" },
      { name: "Bus Stop", distance: "0.2 km" },
      { name: "Railway Station", distance: "2.5 km" },
      { name: "Auto Stand", distance: "0.1 km" },
      { name: "Cab Stand", distance: "0.6 km" }
    ],
    Food: [
      { name: "Restaurant", distance: "0.3 km" },
      { name: "Café", distance: "0.4 km" },
      { name: "Food Court", distance: "1.1 km" },
      { name: "Fast Food", distance: "0.6 km" },
      { name: "Bakery", distance: "0.8 km" }
    ],
    Shopping: [
      { name: "Supermarket", distance: "0.5 km" },
      { name: "Mall", distance: "1.5 km" },
      { name: "Department Store", distance: "0.9 km" },
      { name: "Local Market", distance: "0.3 km" },
      { name: "Convenience Store", distance: "0.2 km" }
    ]
  };

  // Customize based on locality if needed
  if (locality?.toLowerCase().includes("pitampura")) {
    return {
      Utilities: [
        { name: "Pitampura Hospital", distance: "1.0 km" },
        { name: "Apollo Pharmacy", distance: "0.4 km" },
        { name: "HDFC Bank", distance: "0.6 km" },
        { name: "SBI ATM", distance: "0.2 km" },
        { name: "City Clinic", distance: "0.8 km" }
      ],
      Transit: [
        { name: "Pitampura Metro", distance: "0.8 km" },
        { name: "DTC Bus Stop", distance: "0.1 km" },
        { name: "Azadpur Railway", distance: "3.0 km" }
      ],
      Food: [
        { name: "Bikanervala", distance: "0.5 km" },
        { name: "Starbucks", distance: "0.7 km" },
        { name: "Food Plaza", distance: "1.0 km" }
      ],
      Shopping: [
        { name: "Netaji Subhash Place", distance: "1.2 km" },
        { name: "More Supermarket", distance: "0.4 km" },
        { name: "Local Market", distance: "0.2 km" }
      ]
    };
  }

  return defaultData;
};

// Generate next 14 dates
const generateDates = () => {
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  return Array.from({ length: 14 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() + i);
    return {
      dayName: days[d.getDay()],
      date: d.getDate(),
      month: months[d.getMonth()],
      fullDate: d.toISOString().split("T")[0],
    };
  });
};

const TIME_SLOTS = [
  "9:00 – 9:30 AM", "9:30 – 10:00 AM", "10:00 – 10:30 AM", "10:30 – 11:00 AM",
  "11:00 – 11:30 AM", "11:30 – 12:00 PM", "12:00 – 12:30 PM", "2:00 – 2:30 PM",
  "2:30 – 3:00 PM", "3:00 – 3:30 PM", "3:30 – 4:00 PM", "4:00 – 4:30 PM",
  "4:30 – 5:00 PM", "5:00 – 5:30 PM",
];

const SHARING_TYPES = [
  "Single Sharing", "Double Sharing", "Triple Sharing", "Quad Sharing", "Dorm Sharing",
  // "Single Sharing", "Double Sharing", "Triple Sharing",
];

const VISIT_TYPES = [
  {
    key: "video",
    label: "Video Tour",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M15 10l4.553-2.276A1 1 0 0121 8.723v6.554a1 1 0 01-1.447.894L15 14M3 8a2 2 0 012-2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z" />
      </svg>
    ),
  },
  {
    key: "visit",
    label: "Visit Property",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
        <circle cx="12" cy="10" r="3" />
      </svg>
    ),
  },
  {
    key: "call",
    label: "Phone Call",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.8 19.79 19.79 0 01.01 1.18 2 2 0 012 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 14.92v2z" />
      </svg>
    ),
  },
];

// Map Legend Component
const MapLegend = () => (
  <div className="map-legend">
    <div className="legend-title">Map Legend</div>
    <div className="legend-items">
      <div className="legend-item">
        <div className="legend-marker pg-legend"></div>
        <span>PG</span>
      </div>
      <div className="legend-item">
        <div className="legend-marker transit-legend"></div>
        <span>Metro/Bus</span>
      </div>
      <div className="legend-item">
        <div className="legend-marker food-legend"></div>
        <span>Food</span>
      </div>
      <div className="legend-item">
        <div className="legend-marker shopping-legend"></div>
        <span>Shopping</span>
      </div>
      <div className="legend-item">
        <div className="legend-radius"></div>
        <span>2.5km Radius</span>
      </div>
    </div>
  </div>
);

// Booking Modal
// const BookingModal = ({ pgName, packageType, onClose, isLoggedIn = false }) => {
//   const [visitType, setVisitType] = useState("visit");
//   const [selectedDate, setSelectedDate] = useState(null);
//   const [selectedTime, setSelectedTime] = useState(null);
//   const [sharingType, setSharingType] = useState(packageType || "");
//   const [submitted, setSubmitted] = useState(false);
//   const overlayRef = useRef(null);
//   const dates = generateDates();

//   useEffect(() => {
//     document.body.style.overflow = "hidden";
//     return () => {
//       document.body.style.overflow = "";
//     };
//   }, []);

//   useEffect(() => {
//     const h = (e) => {
//       if (e.key === "Escape") onClose();
//     };
//     window.addEventListener("keydown", h);
//     return () => window.removeEventListener("keydown", h);
//   }, [onClose]);

//   const handleOverlayClick = (e) => {
//     if (e.target === overlayRef.current) onClose();
//   };

//   const canSubmit = selectedDate && selectedTime;

//   const handleSubmit = () => {
//     if (!canSubmit) return;
//     setSubmitted(true);
//   };

//   if (submitted) {
//     return (
//       <div className="booking-overlay" ref={overlayRef} onClick={handleOverlayClick}>
//         <div className="booking-modal">
//           <button className="booking-close" onClick={onClose}>
//             <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
//               <path d="M1 1L17 17M17 1L1 17" stroke="#374151" strokeWidth="2" strokeLinecap="round" />
//             </svg>
//           </button>
//           <div className="booking-success">
//             <div className="booking-success-icon">
//               <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
//                 <circle cx="16" cy="16" r="16" fill="#DCFCE7" />
//                 <path d="M9 16.5L13.5 21L23 11" stroke="#16A34A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
//               </svg>
//             </div>
//             <h3 className="booking-success-title">Request Sent!</h3>
//             <p className="booking-success-msg">
//               Your {VISIT_TYPES.find((v) => v.key === visitType)?.label.toLowerCase()} has been scheduled for{" "}
//               <strong>{dates.find((d) => d.fullDate === selectedDate)?.dayName}, {dates.find((d) => d.fullDate === selectedDate)?.date} {dates.find((d) => d.fullDate === selectedDate)?.month}</strong>{" "}
//               at <strong>{selectedTime}</strong>.
//             </p>
//             <p className="booking-success-sub">The property owner will confirm shortly.</p>
//             <button className="booking-submit-btn" onClick={onClose}>Done</button>
//           </div>
//         </div>
//       </div>
//     );
//   }

//   return (
//     <div className="booking-overlay" ref={overlayRef} onClick={handleOverlayClick}>
//       <div className="booking-modal">
//         <div className="booking-modal-header">
//           <div>
//             <h2 className="booking-modal-title">Book a Visit</h2>
//             <p className="booking-modal-subtitle">{pgName}</p>
//           </div>
//           <button className="booking-close" onClick={onClose}>
//             <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
//               <path d="M1 1L17 17M17 1L1 17" stroke="#374151" strokeWidth="2" strokeLinecap="round" />
//             </svg>
//           </button>
//         </div>

//         <div className="booking-modal-body">
//           <div className="booking-section">
//             <p className="booking-section-label">How would you like to visit?</p>
//             <div className="visit-type-grid">
//               {VISIT_TYPES.map((v) => (
//                 <button
//                   key={v.key}
//                   className={`visit-type-card ${visitType === v.key ? "active" : ""}`}
//                   onClick={() => setVisitType(v.key)}
//                 >
//                   <span className="visit-type-icon">{v.icon}</span>
//                   <span className="visit-type-label">{v.label}</span>
//                 </button>
//               ))}
//             </div>
//           </div>

//           <div className="booking-section">
//             <p className="booking-section-label">Select Date</p>
//             <div className="date-scroll-row">
//               {dates.map((d) => (
//                 <button
//                   key={d.fullDate}
//                   className={`date-chip ${selectedDate === d.fullDate ? "active" : ""}`}
//                   onClick={() => setSelectedDate(d.fullDate)}
//                 >
//                   <span className="date-chip-day">{d.dayName}</span>
//                   <span className="date-chip-num">{d.date}</span>
//                   <span className="date-chip-month">{d.month}</span>
//                 </button>
//               ))}
//             </div>
//           </div>

//           <div className="booking-section">
//             <p className="booking-section-label">Select Time Slot</p>
//             <div className="time-scroll-row">
//               {TIME_SLOTS.map((t) => (
//                 <button
//                   key={t}
//                   className={`time-chip ${selectedTime === t ? "active" : ""}`}
//                   onClick={() => setSelectedTime(t)}
//                 >
//                   {t}
//                 </button>
//               ))}
//             </div>
//           </div>

//           <div className="booking-section">
//             <p className="booking-section-label">
//               Sharing Type <span className="booking-optional">(optional)</span>
//             </p>
//             <div className="booking-select-wrapper">
//               <select
//                 className="booking-select"
//                 value={sharingType}
//                 onChange={(e) => setSharingType(e.target.value)}
//               >
//                 <option value="">Choose sharing type</option>
//                 {SHARING_TYPES.map((s) => (
//                   <option key={s} value={s}>{s}</option>
//                 ))}
//               </select>
//               <svg className="booking-select-arrow" width="14" height="14" viewBox="0 0 14 14" fill="none">
//                 <path d="M3 5L7 9L11 5" stroke="#6B7280" strokeWidth="1.6" strokeLinecap="round" />
//               </svg>
//             </div>
//           </div>
//         </div>

//         <div className="booking-modal-footer">
//           {isLoggedIn ? (
//             <button
//               className={`booking-submit-btn ${!canSubmit ? "disabled" : ""}`}
//               onClick={handleSubmit}
//               disabled={!canSubmit}
//             >
//               Confirm Booking
//             </button>
//           ) : (
//             <button className="booking-signup-btn" onClick={onClose}>
//               <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
//                 <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
//                 <circle cx="12" cy="7" r="4" />
//               </svg>
//               Sign up to continue
//             </button>
//           )}
//           {!canSubmit && isLoggedIn && (
//             <p className="booking-hint">Please select a date and time slot to continue</p>
//           )}
//         </div>
//       </div>
//     </div>
//   );
// };

// Booking Modal with OTP Flow

// const BookingModal = ({ pgName, packageType, isLoggedIn = false, pgId, onClose }) => {
//   const navigate = useNavigate(); // used to redirect to /thank-you after a verified booking
//   const [step, setStep] = useState(1); // 1: Booking Details, 2: Signup/Login, 3: OTP Verification, 4: Success (kept, but unreachable now — see handleVerifyOTP)
//   const [visitType, setVisitType] = useState("visit");
//   const [selectedDate, setSelectedDate] = useState(null);
//   const [selectedTime, setSelectedTime] = useState(null);
//   const [sharingType, setSharingType] = useState(packageType || "");
//   const [tenantName, setTenantName] = useState("");
//   const [email, setEmail] = useState("");
//   const [phoneNumber, setPhoneNumber] = useState("");
//   const [otp, setOtp] = useState("");
//   const [bookingId, setBookingId] = useState(null);
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState("");
//   const [otpError, setOtpError] = useState("");
//   const [resendDisabled, setResendDisabled] = useState(false);
//   const [resendTimer, setResendTimer] = useState(0);
//   const [showResendMessage, setShowResendMessage] = useState(false);
//   const overlayRef = useRef(null);
//   const dates = generateDates();
//   const timerRef = useRef(null);

//   useEffect(() => {
//     document.body.style.overflow = "hidden";
//     return () => {
//       document.body.style.overflow = "";
//       if (timerRef.current) clearInterval(timerRef.current);
//     };
//   }, []);

//   useEffect(() => {
//     const h = (e) => {
//       if (e.key === "Escape") onClose();
//     };
//     window.addEventListener("keydown", h);
//     return () => window.removeEventListener("keydown", h);
//   }, [onClose]);

//   // Timer for resend OTP
//   useEffect(() => {
//     if (resendTimer > 0) {
//       timerRef.current = setInterval(() => {
//         setResendTimer((prev) => {
//           if (prev <= 1) {
//             clearInterval(timerRef.current);
//             return 0;
//           }
//           return prev - 1;
//         });
//       }, 1000);
//     }
//     return () => {
//       if (timerRef.current) clearInterval(timerRef.current);
//     };
//   }, [resendTimer]);

//   const handleOverlayClick = (e) => {
//     if (e.target === overlayRef.current) onClose();
//   };

//   const canSubmitBooking = selectedDate && selectedTime;

//   // Handle Create Booking
//   const handleCreateBooking = async () => {
//     if (!canSubmitBooking) return;

//     setLoading(true);
//     setError("");

//     try {

//       console.log("Original time slot:", selectedTime);

//       const bookingData = {
//         PGID: pgId,
//         TenantName: tenantName,
//         Email: email,
//         PhoneNumber: phoneNumber,
//         SharingType: sharingType || "Not Specified",
//         VisitType: visitType === "visit" ? "Physical Visit" : visitType === "video" ? "Video Tour" : "Phone Call",
//         VisitDate: selectedDate,
//         VisitTime: selectedTime
//       };

//       const response = await createVisitBooking(bookingData);

//       if (response.success) {
//         setBookingId(response.data.bookingId);
//         setStep(3); // Move to OTP verification
//         setOtp("");
//         setOtpError("");
//         setResendDisabled(false);
//         setResendTimer(0);
//         setShowResendMessage(false);
//       } else {
//         setError(response.message || "Failed to create booking");
//       }
//     } catch (err) {
//       console.error("Booking error:", err);
//       setError(err.response?.data?.message || "Failed to create booking. Please try again.");
//     } finally {
//       setLoading(false);
//     }
//   };



//   // Handle Resend OTP
//   const handleResendOTP = async () => {
//     if (!bookingId) return;

//     setResendDisabled(true);
//     setResendTimer(30);
//     setShowResendMessage(false);

//     try {
//       const response = await resendOTP({ BookingID: bookingId });
//       if (response.success) {
//         setShowResendMessage(true);
//         setTimeout(() => setShowResendMessage(false), 3000);
//       } else {
//         setOtpError(response.message || "Failed to resend OTP");
//         setResendDisabled(false);
//         setResendTimer(0);
//       }
//     } catch (err) {
//       console.error("Resend OTP error:", err);
//       setOtpError("Failed to resend OTP. Please try again.");
//       setResendDisabled(false);
//       setResendTimer(0);
//     }
//   };

//   // Handle Verify OTP
//   const handleVerifyOTP = async () => {
//     if (!otp || otp.length !== 6) {
//       setOtpError("Please enter a valid 6-digit OTP");
//       return;
//     }

//     setLoading(true);
//     setOtpError("");

//     try {
//       const response = await verifyOTP({ BookingID: bookingId, OTP: otp });

//       if (response.success) {
//         // Build a friendly, human-readable summary for the thank-you page.
//         const dateObj = dates.find((d) => d.fullDate === selectedDate);
//         const formattedDate = dateObj
//           ? `${dateObj.dayName}, ${dateObj.date} ${dateObj.month}`
//           : selectedDate;
//         const visitLabel = VISIT_TYPES.find((v) => v.key === visitType)?.label || "Visit";

//         onClose(); // close the modal
//         navigate("/thank-you", {
//           state: {
//             bookingId,
//             pgName,
//             tenantName,
//             email,
//             phoneNumber,
//             visitLabel,
//             formattedDate,
//             selectedTime,
//           },
//         });
//       } else {
//         setOtpError(response.message || "Invalid OTP. Please try again.");
//       }
//     } catch (err) {
//       console.error("Verify OTP error:", err);
//       setOtpError(err.response?.data?.message || "Failed to verify OTP. Please try again.");
//     } finally {
//       setLoading(false);
//     }
//   };

//   // Step 4: Success Screen
//   if (step === 4) {
//     return (
//       <div className="booking-overlay" ref={overlayRef} onClick={handleOverlayClick}>
//         <div className="booking-modal">
//           <button className="booking-close" onClick={onClose}>
//             <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
//               <path d="M1 1L17 17M17 1L1 17" stroke="#374151" strokeWidth="2" strokeLinecap="round" />
//             </svg>
//           </button>
//           <div className="booking-success">
//             <div className="booking-success-icon">
//               <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
//                 <circle cx="16" cy="16" r="16" fill="#DCFCE7" />
//                 <path d="M9 16.5L13.5 21L23 11" stroke="#16A34A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
//               </svg>
//             </div>
//             <h3 className="booking-success-title">Booking Confirmed!</h3>
//             <p className="booking-success-msg">
//               Your {VISIT_TYPES.find((v) => v.key === visitType)?.label.toLowerCase()} has been scheduled for{" "}
//               <strong>{dates.find((d) => d.fullDate === selectedDate)?.dayName}, {dates.find((d) => d.fullDate === selectedDate)?.date} {dates.find((d) => d.fullDate === selectedDate)?.month}</strong>{" "}
//               at <strong>{selectedTime}</strong>.
//             </p>
//             <p className="booking-success-sub">
//               A confirmation email has been sent to {email}.
//               The property owner will contact you shortly.
//             </p>
//             <button className="booking-submit-btn" onClick={onClose}>Done</button>
//           </div>
//         </div>
//       </div>
//     );
//   }

//   // Step 3: OTP Verification
//   if (step === 3) {
//     return (
//       <div className="booking-overlay" ref={overlayRef} onClick={handleOverlayClick}>
//         <div className="booking-modal">
          

//           <div className="booking-modal-header">
//             <div>
//               <h2 className="booking-modal-title">Verify OTP</h2>
//               <p className="booking-modal-subtitle">
//                 Enter the 6-digit code sent to {email}
//               </p>
//             </div>
//             <button className="booking-close" onClick={onClose}>
//             <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
//               <path d="M1 1L17 17M17 1L1 17" stroke="#374151" strokeWidth="2" strokeLinecap="round" />
//             </svg>
//           </button>
//           </div>

//           <div className="booking-modal-body">
//             <div className="booking-section">
//               <div className="otp-input-group">
//                 <input
//                   type="text"
//                   maxLength="6"
//                   className="otp-input"
//                   placeholder="Enter 6-digit OTP"
//                   value={otp}
//                   onChange={(e) => {
//                     const val = e.target.value.replace(/[^0-9]/g, "");
//                     setOtp(val);
//                     setOtpError("");
//                   }}
//                   autoFocus
//                 />
//                 {otpError && <div className="error-message-text">{otpError}</div>}
//                 {showResendMessage && (
//                   <div className="success-message-text">OTP resent successfully!</div>
//                 )}
//               </div>
//             </div>
//           </div>

//           <div className="booking-modal-footer">
//             <button
//               className={`booking-submit-btn ${(!otp || otp.length !== 6) ? "disabled" : ""}`}
//               onClick={handleVerifyOTP}
//               disabled={!otp || otp.length !== 6 || loading}
//             >
//               {loading ? "Verifying..." : "Verify OTP"}
//             </button>

//             <div className="resend-otp-container">
//               {resendTimer > 0 ? (
//                 <span className="resend-timer">Resend OTP in {resendTimer}s</span>
//               ) : (
//                 <button
//                   className="resend-otp-btn"
//                   onClick={handleResendOTP}
//                   disabled={resendDisabled}
//                 >
//                   Resend OTP
//                 </button>
//               )}
//             </div>
//           </div>
//         </div>
//       </div>
//     );
//   }

//   // Step 2: Signup/Login Form
//   if (step === 2) {
//     const isFormValid = tenantName.trim() && email.trim() && phoneNumber.trim();

//     return (
//       <div className="booking-overlay" ref={overlayRef} onClick={handleOverlayClick}>
//         <div className="booking-modal">
          

//           <div className="booking-modal-header">
//             <div>
//               <h2 className="booking-modal-title">Complete Your Booking</h2>
//               <p className="booking-modal-subtitle">
//                 {pgName} - {packageType || "Select Package"}
//               </p>
//             </div>

//             <button className="booking-close" onClick={onClose}>
//             <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
//               <path d="M1 1L17 17M17 1L1 17" stroke="#374151" strokeWidth="2" strokeLinecap="round" />
//             </svg>
//           </button>
//           </div>

//           <div className="booking-modal-body">
//             <div className="booking-section">
//               <p className="booking-section-label">Booking Summary</p>
//               <div className="booking-summary">
//                 <div className="summary-item">
//                   <span className="summary-label">Visit Type:</span>
//                   <span className="summary-value">
//                     {VISIT_TYPES.find(v => v.key === visitType)?.label}
//                   </span>
//                 </div>
//                 <div className="summary-item">
//                   <span className="summary-label">Date & Time:</span>
//                   <span className="summary-value">
//                     {selectedDate && dates.find(d => d.fullDate === selectedDate)?.dayName}, {selectedDate && dates.find(d => d.fullDate === selectedDate)?.date} {selectedDate && dates.find(d => d.fullDate === selectedDate)?.month} at {selectedTime}
//                   </span>
//                 </div>
//                 {sharingType && (
//                   <div className="summary-item">
//                     <span className="summary-label">Sharing Type:</span>
//                     <span className="summary-value">{sharingType}</span>
//                   </div>
//                 )}
//               </div>
//             </div>

//             <div className="booking-section">
//               <p className="booking-section-label">Your Details</p>
//               <div className="signup-form">
//                 <div className="form-group">
//                   <label className="form-label">Full Name</label>
//                   <input
//                     type="text"
//                     className="form-input"
//                     placeholder="Enter your full name"
//                     value={tenantName}
//                     onChange={(e) => setTenantName(e.target.value)}
//                   />
//                 </div>
//                 <div className="form-group">
//                   <label className="form-label">Email Address</label>
//                   <input
//                     type="email"
//                     className="form-input"
//                     placeholder="you@example.com"
//                     value={email}
//                     onChange={(e) => setEmail(e.target.value)}
//                   />
//                 </div>
//                 <div className="form-group">
//                   <label className="form-label">Phone Number</label>
//                   <input
//                     type="tel"
//                     className="form-input"
//                     placeholder="10-digit mobile number"
//                     value={phoneNumber}
//                     onChange={(e) => {
//                       const val = e.target.value.replace(/[^0-9]/g, "").slice(0, 10);
//                       setPhoneNumber(val);
//                     }}
//                   />
//                 </div>
//                 {error && <div className="error-message-text">{error}</div>}
//               </div>
//             </div>
//           </div>

//           <div className="booking-modal-footer">
//             <button
//               className={`booking-submit-btn ${!isFormValid ? "disabled" : ""}`}
//               onClick={handleCreateBooking}
//               disabled={!isFormValid || loading}
//             >
//               {loading ? "Sending OTP..." : "Send OTP"}
//             </button>
//             <button className="booking-back-btn" onClick={() => setStep(1)}>
//               ← Back
//             </button>
//           </div>
//         </div>
//       </div>
//     );
//   }

//   // Step 1: Booking Details Selection
//   return (
//     <div className="booking-overlay" ref={overlayRef} onClick={handleOverlayClick}>
//       <div className="booking-modal">
//         <div className="booking-modal-header">
//           <div>
//             <h2 className="booking-modal-title">Book a Visit</h2>
//             <p className="booking-modal-subtitle">{pgName}</p>
//           </div>
//           <button className="booking-close" onClick={onClose}>
//             <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
//               <path d="M1 1L17 17M17 1L1 17" stroke="#374151" strokeWidth="2" strokeLinecap="round" />
//             </svg>
//           </button>
//         </div>

//         <div className="booking-modal-body">
//           <div className="booking-section">
//             <p className="booking-section-label">How would you like to visit?</p>
//             <div className="visit-type-grid">
//               {VISIT_TYPES.map((v) => (
//                 <button
//                   key={v.key}
//                   className={`visit-type-card ${visitType === v.key ? "active" : ""}`}
//                   onClick={() => setVisitType(v.key)}
//                 >
//                   <span className="visit-type-icon">{v.icon}</span>
//                   <span className="visit-type-label">{v.label}</span>
//                 </button>
//               ))}
//             </div>
//           </div>

//           <div className="booking-section">
//             <p className="booking-section-label">Select Date</p>
//             <div className="date-scroll-row">
//               {dates.map((d) => (
//                 <button
//                   key={d.fullDate}
//                   className={`date-chip ${selectedDate === d.fullDate ? "active" : ""}`}
//                   onClick={() => setSelectedDate(d.fullDate)}
//                 >
//                   <span className="date-chip-day">{d.dayName}</span>
//                   <span className="date-chip-num">{d.date}</span>
//                   <span className="date-chip-month">{d.month}</span>
//                 </button>
//               ))}
//             </div>
//           </div>

//           <div className="booking-section">
//             <p className="booking-section-label">Select Time Slot</p>
//             <div className="time-scroll-row">
//               {TIME_SLOTS.map((t) => (
//                 <button
//                   key={t}
//                   className={`time-chip ${selectedTime === t ? "active" : ""}`}
//                   onClick={() => setSelectedTime(t)}
//                 >
//                   {t}
//                 </button>
//               ))}
//             </div>
//           </div>

//           <div className="booking-section">
//             <p className="booking-section-label">
//               Sharing Type <span className="booking-optional">(optional)</span>
//             </p>
//             <div className="booking-select-wrapper">
//               <select
//                 className="booking-select"
//                 value={sharingType}
//                 onChange={(e) => setSharingType(e.target.value)}
//               >
//                 <option value="">Choose sharing type</option>
//                 {SHARING_TYPES.map((s) => (
//                   <option key={s} value={s}>{s}</option>
//                 ))}
//               </select>
//               <svg className="booking-select-arrow" width="14" height="14" viewBox="0 0 14 14" fill="none">
//                 <path d="M3 5L7 9L11 5" stroke="#6B7280" strokeWidth="1.6" strokeLinecap="round" />
//               </svg>
//             </div>
//           </div>
//         </div>

//         <div className="booking-modal-footer">
//           <button
//             className={`booking-submit-btn ${!canSubmitBooking ? "disabled" : ""}`}
//             onClick={() => setStep(2)}
//             disabled={!canSubmitBooking}
//           >
//             Continue
//           </button>
//           {!canSubmitBooking && (
//             <p className="booking-hint">Please select a date and time slot to continue</p>
//           )}
//         </div>
//       </div>
//     </div>
//   );
// };

const BookingModal = ({ pgName, packageType, isLoggedIn = false, pgId, onClose }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Booking Details, 2: Signup/Login, 3: OTP Verification
  const [visitType, setVisitType] = useState("visit");
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [sharingType, setSharingType] = useState(packageType || "");
  const [tenantName, setTenantName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [bookingId, setBookingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [otpError, setOtpError] = useState("");
  const [resendDisabled, setResendDisabled] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [showResendMessage, setShowResendMessage] = useState(false);
  const overlayRef = useRef(null);
  const dates = generateDates();
  const timerRef = useRef(null);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  useEffect(() => {
    const h = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  // Timer for resend OTP
  useEffect(() => {
    if (resendTimer > 0) {
      timerRef.current = setInterval(() => {
        setResendTimer((prev) => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [resendTimer]);

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) onClose();
  };

  const canSubmitBooking = selectedDate && selectedTime;

  // Handle Send OTP via MSG91
  const handleSendOTP = async () => {
    const isFormValid = tenantName.trim() && email.trim() && phoneNumber.trim();
    if (!isFormValid) return;

    setLoading(true);
    setError("");

    try {
      // Step 1: Send OTP via MSG91
      const mobileNumber = phoneNumber;
      const otpResponse = await sendMsg91Otp({ mobile: `91${mobileNumber}` });

      if (otpResponse.success) {
        setStep(3); // Move to OTP verification
        setOtp("");
        setOtpError("");
        setResendDisabled(false);
        setResendTimer(0);
        setShowResendMessage(false);
        setLoading(false);
      } else {
        setError(otpResponse.message || "Failed to send OTP");
        setLoading(false);
      }
    } catch (err) {
      console.error("Error in handleSendOTP:", err);
      setError(err.response?.data?.message || err.message || "Failed to send OTP. Please try again.");
      setLoading(false);
    }
  };

  // Handle Resend OTP via MSG91
  const handleResendOTP = async () => {
    if (!phoneNumber) return;

    setResendDisabled(true);
    setResendTimer(30);
    setShowResendMessage(false);

    try {
      const response = await sendMsg91Otp({ mobile: `91${phoneNumber}` });
      
      if (response.success) {
        setShowResendMessage(true);
        setTimeout(() => setShowResendMessage(false), 3000);
        setOtpError("");
      } else {
        setOtpError(response.message || "Failed to resend OTP");
        setResendDisabled(false);
        setResendTimer(0);
      }
    } catch (err) {
      console.error("Resend OTP error:", err);
      setOtpError("Failed to resend OTP. Please try again.");
      setResendDisabled(false);
      setResendTimer(0);
    }
  };

  // Handle Create Booking after OTP verification
  const handleCreateBooking = async () => {
    setLoading(true);
    setError("");

    try {
      const bookingData = {
        PGID: pgId,
        TenantName: tenantName,
        Email: email,
        PhoneNumber: phoneNumber,
        SharingType: sharingType || "Not Specified",
        VisitType: visitType === "visit" ? "Physical Visit" : visitType === "video" ? "Video Tour" : "Phone Call",
        VisitDate: selectedDate,
        VisitTime: selectedTime,
        OTP: otp
      };

      const response = await createVisitBooking(bookingData);

      if (response.success) {
        const newBookingId = response.data.bookingId;
        setBookingId(newBookingId);
        setLoading(false);

        // Build a friendly, human-readable summary for the thank-you page
        const dateObj = dates.find((d) => d.fullDate === selectedDate);
        const formattedDate = dateObj
          ? `${dateObj.dayName}, ${dateObj.date} ${dateObj.month}`
          : selectedDate;
        const visitLabel = VISIT_TYPES.find((v) => v.key === visitType)?.label || "Visit";

        onClose(); // close the modal
        navigate("/thank-you", {
          state: {
            bookingId: newBookingId,
            pgName,
            tenantName,
            email,
            phoneNumber,
            visitLabel,
            formattedDate,
            selectedTime,
          },
        });
      } else {
        setError(response.message || "Failed to create booking");
        setLoading(false);
      }
    } catch (err) {
      console.error("Booking error:", err);
      setError(err.response?.data?.message || "Failed to create booking. Please try again.");
      setLoading(false);
    }
  };

  // Handle Verify OTP via MSG91
  const handleVerifyOTP = async () => {
    if (!otp || otp.length !== 4) {
      setOtpError("Please enter a valid 4-digit OTP");
      return;
    }

    setLoading(true);
    setOtpError("");

    try {
      // Step 1: Verify OTP using MSG91
      const verifyResponse = await verifyMsg91Otp(`91${phoneNumber}`, otp);

      if (verifyResponse.success) {
        setLoading(false);
        // OTP verified successfully! Now create the booking
        await handleCreateBooking();
      } else {
        setOtpError(verifyResponse.message || "Invalid OTP. Please try again.");
        setLoading(false);
      }
    } catch (err) {
      console.error("Verify OTP error:", err);
      setOtpError(err.response?.data?.message || err.message || "Failed to verify OTP. Please try again.");
      setLoading(false);
    }
  };

  // Step 3: OTP Verification
  if (step === 3) {
    return (
      <div className="booking-overlay" ref={overlayRef} onClick={handleOverlayClick}>
        <div className="booking-modal">
          <div className="booking-modal-header">
            <div>
              <h2 className="booking-modal-title">Verify OTP</h2>
              <p className="booking-modal-subtitle">
                Enter the 4-digit code sent to {phoneNumber}
              </p>
            </div>
            <button className="booking-close" onClick={onClose}>
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M1 1L17 17M17 1L1 17" stroke="#374151" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          </div>

          <div className="booking-modal-body">
            <div className="booking-section">
              <div className="otp-input-group">
                <input
                  type="text"
                  maxLength="4"
                  className="otp-input"
                  placeholder="Enter 4-digit OTP"
                  value={otp}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^0-9]/g, "");
                    setOtp(val);
                    setOtpError("");
                  }}
                  autoFocus
                />
                {otpError && <div className="error-message-text">{otpError}</div>}
                {showResendMessage && (
                  <div className="success-message-text">OTP resent successfully!</div>
                )}
              </div>
            </div>
          </div>

          <div className="booking-modal-footer">
            <button
              className={`booking-submit-btn ${(!otp || otp.length !== 4) ? "disabled" : ""}`}
              onClick={handleVerifyOTP}
              disabled={!otp || otp.length !== 4 || loading}
            >
              {loading ? "Verifying..." : "Verify OTP"}
            </button>

            <div className="resend-otp-container">
              {resendTimer > 0 ? (
                <span className="resend-timer">Resend OTP in {resendTimer}s</span>
              ) : (
                <button
                  className="resend-otp-btn"
                  onClick={handleResendOTP}
                  disabled={resendDisabled}
                >
                  Resend OTP
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Step 2: Signup/Login Form
  if (step === 2) {
    const isFormValid = tenantName.trim() && email.trim() && phoneNumber.trim();

    return (
      <div className="booking-overlay" ref={overlayRef} onClick={handleOverlayClick}>
        <div className="booking-modal">
          <div className="booking-modal-header">
            <div>
              <h2 className="booking-modal-title">Complete Your Booking</h2>
              <p className="booking-modal-subtitle">
                {pgName} - {packageType || "Select Package"}
              </p>
            </div>
            <button className="booking-close" onClick={onClose}>
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M1 1L17 17M17 1L1 17" stroke="#374151" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          </div>

          <div className="booking-modal-body">
            <div className="booking-section">
              <p className="booking-section-label">Booking Summary</p>
              <div className="booking-summary">
                <div className="summary-item">
                  <span className="summary-label">Visit Type:</span>
                  <span className="summary-value">
                    {VISIT_TYPES.find(v => v.key === visitType)?.label}
                  </span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Date & Time:</span>
                  <span className="summary-value">
                    {selectedDate && dates.find(d => d.fullDate === selectedDate)?.dayName}, {selectedDate && dates.find(d => d.fullDate === selectedDate)?.date} {selectedDate && dates.find(d => d.fullDate === selectedDate)?.month} at {selectedTime}
                  </span>
                </div>
                {sharingType && (
                  <div className="summary-item">
                    <span className="summary-label">Sharing Type:</span>
                    <span className="summary-value">{sharingType}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="booking-section">
              <p className="booking-section-label">Your Details</p>
              <div className="signup-form">
                <div className="form-group">
                  <label className="form-label">Full Name</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Enter your full name"
                    value={tenantName}
                    onChange={(e) => setTenantName(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Email Address</label>
                  <input
                    type="email"
                    className="form-input"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Phone Number</label>
                  <input
                    type="tel"
                    className="form-input"
                    placeholder="10-digit mobile number"
                    value={phoneNumber}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^0-9]/g, "").slice(0, 10);
                      setPhoneNumber(val);
                    }}
                  />
                </div>
                {error && <div className="error-message-text">{error}</div>}
              </div>
            </div>
          </div>

          <div className="booking-modal-footer">
            <button
              className={`booking-submit-btn ${!isFormValid ? "disabled" : ""}`}
              onClick={handleSendOTP}
              disabled={!isFormValid || loading}
            >
              {loading ? "Sending OTP..." : "Send OTP"}
            </button>
            <button className="booking-back-btn" onClick={() => setStep(1)}>
              ← Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Step 1: Booking Details Selection
  return (
    <div className="booking-overlay" ref={overlayRef} onClick={handleOverlayClick}>
      <div className="booking-modal">
        <div className="booking-modal-header">
          <div>
            <h2 className="booking-modal-title">Book a Visit</h2>
            <p className="booking-modal-subtitle">{pgName}</p>
          </div>
          <button className="booking-close" onClick={onClose}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M1 1L17 17M17 1L1 17" stroke="#374151" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className="booking-modal-body">
          <div className="booking-section">
            <p className="booking-section-label">How would you like to visit?</p>
            <div className="visit-type-grid">
              {VISIT_TYPES.map((v) => (
                <button
                  key={v.key}
                  className={`visit-type-card ${visitType === v.key ? "active" : ""}`}
                  onClick={() => setVisitType(v.key)}
                >
                  <span className="visit-type-icon">{v.icon}</span>
                  <span className="visit-type-label">{v.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="booking-section">
            <p className="booking-section-label">Select Date</p>
            <div className="date-scroll-row">
              {dates.map((d) => (
                <button
                  key={d.fullDate}
                  className={`date-chip ${selectedDate === d.fullDate ? "active" : ""}`}
                  onClick={() => setSelectedDate(d.fullDate)}
                >
                  <span className="date-chip-day">{d.dayName}</span>
                  <span className="date-chip-num">{d.date}</span>
                  <span className="date-chip-month">{d.month}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="booking-section">
            <p className="booking-section-label">Select Time Slot</p>
            <div className="time-scroll-row">
              {TIME_SLOTS.map((t) => (
                <button
                  key={t}
                  className={`time-chip ${selectedTime === t ? "active" : ""}`}
                  onClick={() => setSelectedTime(t)}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          <div className="booking-section">
            <p className="booking-section-label">
              Sharing Type <span className="booking-optional">(optional)</span>
            </p>
            <div className="booking-select-wrapper">
              <select
                className="booking-select"
                value={sharingType}
                onChange={(e) => setSharingType(e.target.value)}
              >
                <option value="">Choose sharing type</option>
                {SHARING_TYPES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              <svg className="booking-select-arrow" width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M3 5L7 9L11 5" stroke="#6B7280" strokeWidth="1.6" strokeLinecap="round" />
              </svg>
            </div>
          </div>
        </div>

        <div className="booking-modal-footer">
          <button
            className={`booking-submit-btn ${!canSubmitBooking ? "disabled" : ""}`}
            onClick={() => setStep(2)}
            disabled={!canSubmitBooking}
          >
            Continue
          </button>
          {!canSubmitBooking && (
            <p className="booking-hint">Please select a date and time slot to continue</p>
          )}
        </div>
      </div>
    </div>
  );
};

// export default BookingModal;


// Lightbox
const Lightbox = ({ images, startIndex, onClose }) => {
  const [current, setCurrent] = useState(startIndex);
  useEffect(() => {
    const h = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setCurrent((c) => (c + 1) % images.length);
      if (e.key === "ArrowLeft") setCurrent((c) => (c - 1 + images.length) % images.length);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [images.length, onClose]);
  return (
    <div className="lightbox-overlay" >
      <button className="lightbox-close" onClick={onClose}>
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M2 2L18 18M18 2L2 18" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </button>
      <button
        className="lightbox-arrow lightbox-prev"
        onClick={(e) => {
          e.stopPropagation();
          setCurrent((c) => (c - 1 + images.length) % images.length);
        }}
      >
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M13 4L7 10L13 16" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </button>
      {isVideoUrl(images[current]) ? (
        <video
          key={images[current]}
          src={images[current]}
          controls
          autoPlay
          playsInline
          className="lightbox-video"
          onClick={(e) => e.stopPropagation()}
        />
      ) : (
        <img src={images[current]} alt={`Room ${current + 1}`} onClick={(e) => e.stopPropagation()} />
      )}
      <button
        className="lightbox-arrow lightbox-next"
        onClick={(e) => {
          e.stopPropagation();
          setCurrent((c) => (c + 1) % images.length);
        }}
      >
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M7 4L13 10L7 16" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </button>
      <span className="lightbox-counter">{current + 1} / {images.length}</span>
    </div>
  );
};

// Package Card
const PREVIEW_COUNT = 3;

const PackageCard = ({ pkg, onBook, onPhotoClick }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const previewAmenities = pkg.roomAmenities?.slice(0, PREVIEW_COUNT) || [];
  const extraAmenities = pkg.roomAmenities?.slice(PREVIEW_COUNT) || [];
  const hasMore = extraAmenities.length > 0;

  // Get PhotoType from package type
  const getPhotoTypeFromPackage = (type) => {
    if (type === "Single Sharing") return "SINGLE";
    if (type === "Double Sharing") return "DOUBLE";
    if (type === "Triple Sharing") return "TRIPLE";
    if (type === "Quad Sharing") return "QUAD";
    if (type === "Dorm Sharing") return "DORM";
    return "PG";
  };

  const photoType = getPhotoTypeFromPackage(pkg.type);

  return (
    <>
      <div className="package-card">

        <div
          className="package-photo-wrapper"
          onClick={() => onPhotoClick(photoType)}
        >
          <MediaItem
            src={pkg.photoURL || "/noimage.jpg"}
            alt={pkg.type}
            className="package-photo"
            showPlayBadge
          />
          <div className="package-photo-overlay">
            <span>View Photos</span>
          </div>
        </div>


        <div className="package-header">
          <h3 className="package-type">{pkg.type}</h3>
          {/* <span className="package-availability">
            {pkg.available} of {pkg.total} available
          </span> */}
        </div>
        {/* <div className="package-price">
          <span className="package-price-amount">
            ₹{pkg.price.toLocaleString("en-IN")}
          </span>
          <span className="package-price-unit">/month</span>
        </div> */}
        <div className="package-price">
          {pkg.price && pkg.price > 0 ? (
            <>
              <span className="package-price-amount">
                ₹{pkg.price.toLocaleString("en-IN")}
              </span>
              <span className="package-price-unit">/month</span>
            </>
          ) : (
            <span className="package-price-na">Price on Request</span>
          )}
        </div>
        <ul className="package-amenity-list">
          {previewAmenities.map((item, i) => (
            <li key={i} className="package-amenity-item">
              <span className="amenity-check-circle">
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                  <path d="M2 5L4.2 7.5L8 3" stroke="#16A34A" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
              {item}
            </li>
          ))}
        </ul>
        {hasMore && (
          <button className="package-view-all" onClick={() => setModalOpen(true)}>
            +{extraAmenities.length} more amenities ↗
          </button>
        )}
        <button className="package-cta" onClick={() => onBook(pkg.type)}>
          I'm Interested
        </button>
      </div>

      {modalOpen && (
        <div className="pkg-modal-overlay" onClick={() => setModalOpen(false)}>
          <div className="pkg-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pkg-modal-header">
              <div>
                <h3 className="pkg-modal-title">{pkg.type}</h3>
                {/* <p className="pkg-modal-price">₹{pkg.price.toLocaleString("en-IN")}/month</p> */}
                <p className="pkg-modal-price">
                  {pkg.price && pkg.price > 0 ?
                    `₹${pkg.price.toLocaleString("en-IN")}/month` :
                    "Price on Request"}
                </p>
              </div>
              <button className="pkg-modal-close" onClick={() => setModalOpen(false)}>
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M1 1L17 17M17 1L1 17" stroke="#374151" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
            </div>
            <ul className="pkg-modal-list">
              {pkg.roomAmenities.map((item, i) => (
                <li key={i} className="package-amenity-item">
                  <span className="amenity-check-circle">
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                      <path d="M2 5L4.2 7.5L8 3" stroke="#16A34A" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                  {item}
                </li>
              ))}
            </ul>
            <button className="package-cta" onClick={() => {
              setModalOpen(false);
              onBook(pkg.type);
            }}>
              I'm Interested
            </button>
          </div>
        </div>
      )}
    </>
  );
};

// Similar Property Card
const SimilarCard = ({ pg, currentPGId }) => {
  const navigate = useNavigate();
  const [saved, setSaved] = useState(false);

  const gClass = pg.PGType === "Boys" ? "gender-boys" :
    pg.PGType === "Girls" ? "gender-girls" : "gender-coed";

  const roomTypes = [
    pg.RoomType1,
    pg.RoomType2,
    pg.RoomType3
  ].filter(room => room && room.trim() !== '');

  const amenities = [
    pg.Category1,
    pg.Category2,
    pg.Category3
  ].filter(amenity => amenity && amenity.trim() !== '');

  const getImageUrl = () => {
    if (pg.PGPhoto && pg.PGPhoto !== 'null' && pg.PGPhoto !== '') {
      return pg.PGPhoto;
    }
    if (pg.PGType?.includes('Girls')) {
      return 'https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=600&h=400&fit=crop&auto=format';
    }
    if (pg.PGType?.includes('Boys')) {
      return 'https://images.unsplash.com/photo-1611892440504-42a792e24d32?w=600&h=400&fit=crop&auto=format';
    }
    return 'https://images.unsplash.com/photo-1540518614846-7eded433c457?w=600&h=400&fit=crop&auto=format';
  };

  const occupancy = {
    filled: Math.floor(Math.random() * 15) + 5,
    total: 20
  };

  return (
    <div className="similar-card" onClick={() => navigate(`/pg/${(pg.PGName || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')}-${pg.PGID}`, { state: { PGNAME: pg.PGName, PGID: pg.PGID } })}>
      <div className="similar-card-image">
        <img src={getImageUrl()} alt={pg.PGName} loading="lazy" />
        <span className={`card-gender-badge ${gClass}`}>{pg.PGType}</span>
        <button
          className={`card-save-btn ${saved ? "saved" : ""}`}
          onClick={(e) => {
            e.stopPropagation();
            setSaved((s) => !s);
          }}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill={saved ? "#155DFC" : "none"}
            stroke={saved ? "#155DFC" : "#fff"}
            strokeWidth="2"
          >
            <path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z" />
          </svg>
        </button>
      </div>
      <div className="similar-card-body">
        <div className="similar-name-row">
          <h4 className="similar-card-name">{pg.PGName}</h4>
          <span className="similar-occupancy">
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
            </svg>
            {occupancy.filled}/{occupancy.total}
          </span>
        </div>
        <p className="similar-location">
          <svg
            width="11"
            height="11"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
            <circle cx="12" cy="10" r="3" />
          </svg>
          {pg.PGLocality || pg.pglocality}, {pg.PGCity || 'Delhi'}
        </p>

        {roomTypes.length > 0 && (
          <div className="card-pills">
            {roomTypes.slice(0, 3).map((t, idx) => (
              <span key={idx} className="card-pill">
                {t}
              </span>
            ))}
            {roomTypes.length > 3 && (
              <span className="card-pill card-pill-more">
                +{roomTypes.length - 3} more
              </span>
            )}
          </div>
        )}

        {amenities.length > 0 && (
          <div className="card-pills card-amenities">
            {amenities.slice(0, 3).map((a, idx) => (
              <span key={idx} className="card-pill-amenity">
                {a}
              </span>
            ))}
            {amenities.length > 3 && (
              <span className="card-pill card-pill-more">
                +{amenities.length - 3} more
              </span>
            )}
          </div>
        )}

        <div className="similar-divider" />

        <div className="similar-footer">
          <div>
            {pg.PGRent ? (
              <>
                <span className="similar-price">
                  ₹{pg.PGRent.toLocaleString("en-IN")}
                </span>
                <span className="similar-unit">/month</span>
              </>
            ) : (
              <span className="similar-contact">For rent contact PG</span>
            )}
          </div>
          <button
            className="similar-btn"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/pg/${(pg.PGName || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')}-${pg.PGID}`, { state: { PGNAME: pg.PGName, PGID: pg.PGID } })
            }
            }
          >
            View Details
          </button>
        </div>
      </div>
    </div>
  );
};

// NearbyMarkers with category icons and tooltips
const NearbyMarkers = ({ markers = [], category }) => {
  if (!markers.length) return null;

  return (
    <>
      {markers.map((m, i) => {
        const markerIcon = getCategoryMarker(category, m.name, m.tags || {});
        return (
          <Marker
            key={`${category}-${m.name}-${m.lat}-${m.lng}-${i}`}
            position={[m.lat, m.lng]}
            icon={markerIcon}
          >
            <Popup className="nearby-popup" closeButton={true} autoPan={true}>
              <div className="nearby-popup-content">
                <div className="nearby-popup-icon">
                  {category === "Transit" && m.type === "metro" && "🚇"}
                  {category === "Transit" && m.type === "bus" && "🚌"}
                  {category === "Transit" && (!m.type || m.type === "transit") && "🚏"}
                  {category === "Food" && "🍔"}
                  {category === "Shopping" && "🛒"}
                  {category === "Utilities" && "🏥"}
                </div>
                <div className="nearby-popup-details">
                  <strong className="nearby-popup-name">{m.name}</strong>
                  <span className="nearby-popup-distance">📍 {m.distance} away</span>
                </div>
              </div>
            </Popup>
            <Tooltip
              direction="top"
              offset={[0, -20]}
              opacity={0.9}
              permanent={false}
              className="custom-tooltip"
            >
              <span>{m.name}</span>
            </Tooltip>
          </Marker>
        );
      })}
    </>
  );
};

// Map Controller for bounds fitting
const MapController = ({ markers, pgPosition }) => {
  const map = useMap();

  useEffect(() => {
    if (!markers?.length && !pgPosition) return;
    const allPoints = [];
    if (pgPosition) allPoints.push([pgPosition.lat, pgPosition.lng]);
    markers.forEach((m) => allPoints.push([m.lat, m.lng]));
    if (allPoints.length > 0) {
      map.fitBounds(allPoints, { padding: [50, 50], maxZoom: 15, animate: true });
    }
  }, [markers, pgPosition, map]);

  return null;
};

// overflow text clamp about property/owner
const ClampedText = ({ text, className = "", lines = 3 }) => {
  const ref = useRef(null);
  const [overflows, setOverflows] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const measure = () => {
      el.style.webkitLineClamp = "unset";
      el.style.overflow = "visible";
      el.style.display = "block";
      const fullHeight = el.scrollHeight;
      el.style.webkitLineClamp = "";
      el.style.overflow = "";
      el.style.display = "";
      const lineHeight = parseFloat(getComputedStyle(el).lineHeight);
      setOverflows(fullHeight > lineHeight * lines + 2);
    };

    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [text, lines]);

  return (
    <>
      <p ref={ref} className={`${className} ${expanded ? "" : "clamped"}`}>
        {text}
      </p>
      {overflows && (
        <button className="read-more-btn" onClick={() => setExpanded((s) => !s)}>
          {expanded ? "Show less ↑" : "Read more ↓"}
        </button>
      )}
    </>
  );
};

// Icon mapping for amenities
const amenityIcons = {
  "wifi": "📶",
  "parking": "🅿️",
  "ac": "❄️",
  "cctv": "📹",
  "lift": "🛗",
  "ro water": "🚰",
  "gym": "🏋️",
  "power backup": "🔋",
  "housekeeping": "🧹",
  "laundry service": "🧺",
  "food included": "🍽️",
  "security guard": "🛡️",
  "biometric entry": "🔐",
  "hot water": "🚿",
  "refrigerator": "🧊",
  "microwave": "📡",
  "induction stove": "🍳",
  "tv": "📺",
  "common kitchen": "🍳",
  "24x7 water supply": "💧",
  "fire safety": "🧯",
  "first aid kit": "⛑️",
  "common washroom": "🚻",
  "first aid box": "⛑️",
  "warden": "👮",
  "almirah": "🗄️",
  "geyser": "🔥",
  "food": "🍛",
  "common terrace": "🏞️",
  "washing machine": "🧼",
  "chair": "🪑",
};

// Helper to get icon, with a fallback default
const getAmenityIcon = (name) => {
  if (!name) return "✓";
  return amenityIcons[name.trim().toLowerCase()] || "✓";
};

// ─── Media type detection ───────────────────────────────────────────
// The API returns videos mixed in with photos in the same array (same
// PhotoType, e.g. "PG"). The only way to tell them apart is the file
// extension on PhotoURL, so we detect it client-side against known
// video/image extension lists.
const VIDEO_EXTENSIONS = [
  ".mp4", ".webm", ".mov", ".m4v", ".ogv", ".ogg",
  ".avi", ".wmv", ".flv", ".mkv", ".3gp", ".3g2", ".mpeg", ".mpg",
];
const IMAGE_EXTENSIONS = [
  ".png", ".jpg", ".jpeg", ".gif", ".svg", ".webp", ".avif",
  ".bmp", ".tiff", ".tif", ".heic", ".heif", ".ico",
];

// Strips query string/hash and pulls the trailing ".ext" off a URL.
const getUrlExtension = (url = "") => {
  const clean = url.split("?")[0].split("#")[0].toLowerCase();
  const match = clean.match(/\.[a-z0-9]+$/);
  return match ? match[0] : "";
};

const isVideoUrl = (url = "") => VIDEO_EXTENSIONS.includes(getUrlExtension(url));
const isImageUrl = (url = "") => IMAGE_EXTENSIONS.includes(getUrlExtension(url));

// Anything that's neither a known video nor image extension still
// renders as an <img> (the safer fallback — a broken image icon beats
// a broken video player), but we log it so a genuinely new file type
// coming from the API gets noticed instead of silently mis-rendering.
const warnIfUnrecognizedMediaType = (url) => {
  if (!url || isVideoUrl(url) || isImageUrl(url)) return;
  console.warn(`[Media] Unrecognized file extension, defaulting to <img>: ${url}`);
};

// Returns the first URL in a list that is NOT a video, falling back to
// the first URL overall. Used anywhere we need a static thumbnail
// (e.g. the map marker popup) and can't play a <video> there.
const getFirstImageUrl = (urls = []) => {
  if (!urls.length) return null;
  return urls.find((u) => !isVideoUrl(u)) || urls[0];
};

// ─── Video poster-frame selection ──────────────────────────────────
// Picks a frame to show as a video's static thumbnail. Instead of a
// single fixed number of seconds (which is wrong for any video whose
// length differs from whatever it was tuned for — a 17.5s "good"
// frame is deep into a 10s clip's black outro), we seek to a FRACTION
// of the video's own duration, so it self-adjusts per video.
//
// If canvas pixel sampling is available, we go further: sample the
// candidate frame's average brightness, and if it's essentially black,
// try the next candidate fraction (up to a few attempts) until we land
// on one that isn't. This needs the video to be readable by canvas —
// see the crossOrigin note on MediaItem below for when that works.
const POSTER_CANDIDATE_FRACTIONS = [0.1, 0.25, 0.4, 0.6, 0.8];
const POSTER_BLACK_THRESHOLD = 16; // 0 (black) – 255 (white)

const sampleFrameBrightness = (video) => {
  try {
    const canvas = document.createElement("canvas");
    canvas.width = 32;
    canvas.height = 18;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, 32, 18);
    const { data } = ctx.getImageData(0, 0, 32, 18);
    let total = 0;
    for (let i = 0; i < data.length; i += 4) {
      total += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    }
    return total / (data.length / 4);
  } catch (e) {
    // Cross-origin video without CORS headers taints the canvas —
    // pixel sampling isn't possible. Caller should just accept the
    // frame it already seeked to.
    return null;
  }
};

const pickBestPosterFrame = (video, fractions = POSTER_CANDIDATE_FRACTIONS) => {
  const duration = video.duration;
  if (!duration || !isFinite(duration)) return;

  let i = 0;
  const seekToNext = () => {
    if (i >= fractions.length) return; // out of attempts — keep whatever frame we're on
    const t = Math.min(duration - 0.1, Math.max(0.1, duration * fractions[i]));
    video.currentTime = t;
  };

  const onSeeked = () => {
    const brightness = sampleFrameBrightness(video);
    const goodEnough = brightness === null || brightness > POSTER_BLACK_THRESHOLD;
    if (goodEnough || i === fractions.length - 1) {
      video.removeEventListener("seeked", onSeeked);
      return;
    }
    i += 1;
    seekToNext();
  };

  video.addEventListener("seeked", onSeeked);
  seekToNext();
};

// Drop-in replacement for <img>: renders a <video> when the src is a
// video file, otherwise renders a normal <img>.
//
// Thumbnails (hero/gallery thumbs/package cards) NEVER autoplay here,
// on purpose: object-fit:cover crops the video to fill its box exactly
// like it does for photos, and a cropped *static* frame reads as a
// normal thumbnail crop, but a cropped frame that's also *moving*
// looks broken. Actual playback (uncropped, object-fit left as the
// video's natural size) only happens in the lightbox/modal.
//
// Which frame is shown as the "thumbnail" is controllable a few ways:
// - `poster`: pass a real poster image URL (best option — instant, no
//   extra video byte-range fetch, works consistently across browsers).
//   Use this if/when the API ever returns a thumbnail image for a
//   video alongside PhotoURL.
// - default (no `poster`, no `posterTime`): auto-picks a frame as a
//   FRACTION of each video's own duration (10%, then 25%/40%/60%/80%
//   if brightness sampling is available and the earlier ones are
//   black) — see pickBestPosterFrame above. This self-adjusts per
//   video instead of using one fixed number of seconds.
// - `posterTime`: force an exact number of seconds in, overriding the
//   auto behavior, if you know a specific video needs a specific frame.
// - `crossOrigin="anonymous"`: only pass this if your video storage
//   sends proper CORS headers (Access-Control-Allow-Origin) for these
//   files. It's required for the brightness-based "skip black frames"
//   step to work at all; without it that step silently no-ops and you
//   still get the duration-fraction default, which already adapts to
//   each video's length. Setting this without CORS actually configured
//   server-side can break video loading entirely, so it's opt-in.
const MediaItem = ({ src, alt = "", className = "", showPlayBadge = false, controls = false, autoPlay = false, poster, posterTime, crossOrigin, ...rest }) => {
  const videoRef = useRef(null);

  useEffect(() => {
    if (!src) return;
    warnIfUnrecognizedMediaType(src);
  }, [src]);

  useEffect(() => {
    if (controls || autoPlay || poster) return; // playing/controlled elsewhere, or a real poster already covers this
    const v = videoRef.current;
    if (!v) return;
    const onLoadedMetadata = () => {
      if (posterTime != null) {
        try { v.currentTime = posterTime; } catch (e) { /* ignore */ }
        return;
      }
      pickBestPosterFrame(v);
    };
    v.addEventListener("loadedmetadata", onLoadedMetadata);
    return () => v.removeEventListener("loadedmetadata", onLoadedMetadata);
  }, [src, controls, autoPlay, poster, posterTime]);

  if (isVideoUrl(src)) {
    return (
      <span className={`media-video-wrapper ${className}`}>
        <video
          ref={videoRef}
          className="media-video"
          src={src}
          poster={poster}
          crossOrigin={crossOrigin}
          muted={!controls}
          autoPlay={autoPlay}
          loop={!controls}
          playsInline
          controls={controls}
          preload="metadata"
          {...rest}
        />
        {showPlayBadge && (
          <span className="media-play-badge" aria-hidden="true">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="#fff">
              <path d="M8 5v14l11-7z" />
            </svg>
          </span>
        )}
      </span>
    );
  }
  return <img src={src} alt={alt} className={className} {...rest} />;
};

// Main PG Detail Component
const PGDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { pgSlug: pgName } = useParams();
  const { location: searchLocation } = useSearch();

  const [photoGalleryOpen, setPhotoGalleryOpen] = useState(false);

  // Which sub-view PhotoGalleryModal opens into, and which photo is
  // selected when it opens straight into the lightbox.
  //
  // We identify the target photo by its PhotoID (a stable ID from the
  // API) rather than by URL or array index. URLs can contain characters
  // that need encoding/normalizing and could theoretically collide or
  // shift; array position depends on two independently-built arrays
  // staying in sync. PhotoID is the actual unique key the API already
  // gives us for this exact purpose — use it directly.
  const [photoGalleryViewMode, setPhotoGalleryViewMode] = useState("grid");
  const [photoGallerySelectedPhotoId, setPhotoGallerySelectedPhotoId] = useState(null);

  // Central place to open the photo/video gallery.
  // - viewMode "lightbox": jumps straight to the full-view/player on
  //   `photoId` (used when someone clicks an individual hero/thumb
  //   image or video)
  // - viewMode "grid": opens the masonry browse view (used for the
  //   "+X Photos" overlay and package "View Photos" clicks)
  const openGallery = (photoId, viewMode = "lightbox", tab = "All") => {
    console.log("[Gallery] openGallery called →", { photoId, viewMode, tab });
    setSelectedPhotoTab(tab);
    setPhotoGallerySelectedPhotoId(photoId ?? null);
    setPhotoGalleryViewMode(viewMode);
    setPhotoGalleryOpen(true);
  };

  const handlePackagePhotoClick = (photoType) => {
    // Package "View Photos" still opens the browsable grid, filtered to
    // that room type — there isn't one specific photo the user clicked.
    openGallery(null, "grid", photoType);
  };

  // State for selected tab
  const [selectedPhotoTab, setSelectedPhotoTab] = useState("All");


  const [activeTab, setActiveTab] = useState("About");
  const [amenityTab, setAmenityTab] = useState("Common");
  const [nearbyTab, setNearbyTab] = useState("Transit");
  const [nearbyPlaces, setNearbyPlaces] = useState(null);
  const [nearbyMarkers, setNearbyMarkers] = useState({});
  const [nearbyLoading, setNearbyLoading] = useState(false);
  const [showFullAbout, setShowFullAbout] = useState(false);
  const [showFullOwner, setShowFullOwner] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [saved, setSaved] = useState(false);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [bookingPackage, setBookingPackage] = useState("");
  const [pgData, setPgData] = useState(null);
  const [similarPGs, setSimilarPGs] = useState([]);
  const [similarLoading, setSimilarLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const retryTimeoutRef = useRef(null);
  const [mapReady, setMapReady] = useState(false);
  const [showAllNearby, setShowAllNearby] = useState({});
  const [landmarks, setLandmarks] = useState([]);

  const isLoggedIn = false;
  const sectionRefs = useRef({});
  const tabsRef = useRef(null);
  const isMountedRef = useRef(true);

  // Add this helper function at the top of your PGDetail component (after all useState declarations)
  const extractPGIdFromUrl = useCallback((urlId) => {
    if (!urlId) return null;

    // If it's already a number, return it
    if (!isNaN(urlId)) return parseInt(urlId);

    // Try to extract number from slug like "Sunshine-PG-15"
    const matches = urlId.match(/(\d+)$/);
    if (matches) {
      return parseInt(matches[1]);
    }

    return null;
  }, []);

  // Then REPLACE your existing fetchPGData useEffect with this:
  useEffect(() => {
    const fetchPGData = async () => {
      setLoading(true);
      setError(null);

      try {
        let pgid = null;

        // Priority 1: From location state (when coming from listings)
        if (location.state?.PGID) {
          pgid = location.state.PGID;
        }
        // Priority 2: Extract from URL param (pgName) - for direct access
        else if (pgName) {
          // You'll need to extract ID from the name or fetch by name
          pgid = extractPGIdFromUrl(pgName);
        }

        if (!pgid) {
          setError("Invalid property ID");
          setLoading(false);
          return;
        }

        const response = await postTenantPage({ PGID: pgid });
        const transformedData = transformPgData(response, pgid);
        console.log(transformedData);
        setPgData(transformedData);

    // ----- LANDMARKS LOGIC - USING SEARCH CONTEXT -----
    // Use search location from context, fallback to PG locality
    const searchLoc = searchLocation || transformedData.location;
    
    const landmarks = getLandmarksForLocality(searchLoc);
    setLandmarks(landmarks);
    // ----- END LANDMARKS LOGIC -----


        if (transformedData.location) {
          await fetchSimilarPGs(transformedData.location, pgid);
        }

        if (transformedData.lat && transformedData.lng) {
          fetchNearbyPlacesNonBlocking(transformedData.lat, transformedData.lng, transformedData.location);
        } else {
          setNearbyPlaces(getStaticNearbyData(transformedData.location));
          setNearbyLoading(false);
        }

      } catch (err) {
        console.error("Error fetching PG data:", err);
        setError("Failed to load property details. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    if (pgName || location.state?.PGID) {
      fetchPGData();
    } else {
      setError("Property not found");
      setLoading(false);
    }

    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, [pgName, location.state, extractPGIdFromUrl]);

  // Fetch similar PGs based on locality
  const fetchSimilarPGs = async (locality, currentPGId) => {
    try {
      setSimilarLoading(true);
      const requestBody = {
        PGCity: 'Delhi',
        PGLocality: locality
      };

      const response = await getLandingPageForTenant(requestBody);

      let pgData = [];
      if (response && Array.isArray(response)) {
        pgData = response;
      } else if (response && response.data) {
        pgData = response.data;
      } else if (response && response.recordsets && response.recordsets[0]) {
        pgData = response.recordsets[0];
      }

      const filteredPGs = pgData
        .filter(pg => pg.PGID !== currentPGId)
        .slice(0, 6);

      setSimilarPGs(filteredPGs);
    } catch (error) {
      console.error('Error fetching similar PGs:', error);
      setSimilarPGs([]);
    } finally {
      setSimilarLoading(false);
    }
  };

  // Transform API response to component format
  const transformPgData = (apiResponse, pgid) => {
    console.log("API Response for PGID", pgid, ":", apiResponse);
    const pgDetails = apiResponse[0]?.[0] || {};
    console.log("pgDetails", pgDetails);
    const roomTypes = apiResponse[1] || [];
    console.log("roomTypes", roomTypes);
    const propertyPhotos = apiResponse[2] || [];
    console.log("propertyPhotos", propertyPhotos);
    const pgAdditionalInfo = apiResponse[3]?.[0] || {};
    const equipmentCategories = apiResponse[4] || [];
    console.log("equipmentCategories", equipmentCategories);
    const rules = apiResponse[5] || [];
    const occupancyData = apiResponse[6]?.[0] || {};
    console.log("occupancyData", occupancyData);



    // Parse latitude and longitude from API response
    const latitude = parseFloat(pgDetails.Latitude);
    const longitude = parseFloat(pgDetails.Longitude);

    // Default coordinates for Delhi if parsing fails
    const defaultLat = 28.6139;
    const defaultLng = 77.2090;

    const finalLat = !isNaN(latitude) && latitude !== 0 ? latitude : defaultLat;
    const finalLng = !isNaN(longitude) && longitude !== 0 ? longitude : defaultLng;

    // Group amenities by type
    const amenities = {
      Common: [],
      Services: [],
      Food: []
    };

    // equipmentCategories.forEach(cat => {
    //   if (cat.Type === "PG") {
    //     amenities.Common.push(cat.CategoryName);
    //   } else if (cat.Type === "DOUBLE" || cat.Type === "TRIPLE" || cat.Type === "SINGLE") {
    //     // amenities.Services.push(cat.CategoryName);
    //     amenities.Common.push(cat.CategoryName);
    //   }
    // });

    equipmentCategories.forEach(cat => {
      if (cat.Type === "PG") {
        amenities.Common.push(cat.CategoryName);
      }
    });

    // amenities.Common = [...new Set(amenities.Common)];
    amenities.Common = [...new Set(amenities.Common)];
    console.log("PG Amenities only:", amenities.Common);

    amenities.Services = [...new Set(amenities.Services)];
    console.log("Transformed PG amenities:", amenities);
    console.log("Transformed PG roomTypes:", roomTypes);

    // Build packages from room types
    // const packages = roomTypes.map(rt => {
    //   // const rent = parseInt(rt.Rent) || 0;
    //   const rent = rt.Rent ? parseInt(rt.Rent) : null; // Allow null/undefined
    //   let availability = { available: 0, total: 0 };

    //   if (occupancyData && occupancyData[rt.roomtype]) {
    //     const occString = occupancyData[rt.roomtype];
    //     const parts = occString.split('/');
    //     availability.available = parseInt(parts[0]) || 0;
    //     availability.total = parseInt(parts[1]) || 0;
    //   }

    //   const roomAmenities = equipmentCategories
    //     .filter(cat => cat.Type === rt.roomtype)
    //     .map(cat => cat.CategoryName);


    //   const photo = propertyPhotos.find(p => p.PhotoType === rt.roomtype);
    //   const photoURL = photo?.PhotoURL || null;

    //   return {
    //     type: rt.roomtype === "SINGLE" ? "Single Sharing" :
    //       rt.roomtype === "DOUBLE" ? "Double Sharing" :
    //         rt.roomtype === "TRIPLE" ? "Triple Sharing" : rt.roomtype,
    //     price: rent,
    //     available: availability.available,
    //     total: availability.total,
    //     roomAmenities: [...new Set(roomAmenities)],
    //     photoURL: photoURL
    //   };
    // });

    const packages = roomTypes.map(rt => {
      // Map roomtype to display name
      const typeMap = {
        'SINGLE SHARING': 'Single Sharing',
        'DOUBLE SHARING': 'Double Sharing',
        'TRIPLE SHARING': 'Triple Sharing',
        'QUAD SHARING': 'Quad Sharing',
        'DORM SHARING': 'Dorm Sharing'
      };

      // Get display type
      const displayType = typeMap[rt.roomtype] || rt.roomtype;

      // Map to PhotoType for filtering amenities
      const photoTypeMap = {
        'Single Sharing': 'SINGLE',
        'Double Sharing': 'DOUBLE',
        'Triple Sharing': 'TRIPLE',
        'Quad Sharing': 'QUAD',
        'Dorm Sharing': 'DORM'
      };


      const photoType = photoTypeMap[displayType] || rt.roomtype;

      console.log("photoType", photoType);

      // Filter amenities for this room type
      const roomAmenities = equipmentCategories
        .filter(cat => cat.Type === photoType)
        .map(cat => cat.CategoryName);

      console.log("roomAmenities", roomAmenities);

      // Find photo for this room type
      const photo = propertyPhotos.find(p => p.PhotoType === photoType);
      const photoURL = photo?.PhotoURL || null;

      // Rent from roomTypes
      const rent = rt.Rent ? parseInt(rt.Rent) : null;

      return {
        type: displayType,
        price: rent,
        available: 0,
        total: 0,
        roomAmenities: [...new Set(roomAmenities)],
        photoURL: photoURL
      };
    });


    // const validPackages = packages.filter(p => p.price > 0);
    // const validPackages = packages
    // console.log("validPackages", validPackages);

    const validPackages = packages.filter(p => p.price !== null && p.price > 0);

    console.log("Packages with amenities:", validPackages);


    const propertyRules = rules
      // .filter(r => r.RulesType === "PROPERTY")
      .filter(r => r.RulesType === "PROPERTY" || r.RulesType === "CUSTOM")
      .map(r => r.Rules);

    const ownerInfo = rules.find(r => r.RulesType === "OWNER");
    console.log("pgAdditionalInfo", pgAdditionalInfo);

    // const aboutUsInfo = rules.find(r => r.RulesType === "ABOUT US");
    const aboutUsInfo = pgAdditionalInfo.aboutproperty;




      // ✅ SORT PHOTOS: Primary first, then by DisplayOrder
    const sortedPhotos = [...propertyPhotos].sort((a, b) => {
      // Primary photos come first
      if (a.IsPrimary === 1 && b.IsPrimary !== 1) return -1;
      if (b.IsPrimary === 1 && a.IsPrimary !== 1) return 1;
      // Then sort by DisplayOrder (ascending)
      return (a.DisplayOrder || 0) - (b.DisplayOrder || 0);
    });

    console.log("Sorted Photos:", sortedPhotos);


    // const galleryImages = propertyPhotos.map(photo =>
    //   photo.PhotoURL.startsWith('http') ? photo.PhotoURL : `http://localhost:3000${photo.PhotoURL}`
    // );

    // Gallery images from sorted photos
  // IMPORTANT: this same normalization must be applied to every array
  // that carries a PhotoURL (galleryImages AND rawPhotos below). If the
  // two arrays normalize differently, a URL built from one won't
  // string-match the URL stored in the other — which is exactly what
  // was causing the lightbox to always open the wrong (usually first)
  // photo/video regardless of what was clicked.
  // Base URL used only to resolve relative PhotoURL values from the API
  // (most photo URLs are already absolute, e.g. the azurewebsites CDN,
  // so this branch rarely fires — but when it does, it must NOT be a
  // hardcoded dev host, or every relative photo breaks in production.
  // Set REACT_APP_API_BASE_URL in your .env (dev/staging/prod each get
  // their own value); if it's unset we fall back to the current origin
  // rather than silently pointing at localhost.
  const PHOTO_BASE_URL =
    process.env.REACT_APP_API_BASE_URL ||
    (typeof window !== "undefined" ? window.location.origin : "");

  const normalizePhotoUrl = (url) =>
    url && url.startsWith('http') ? url : `${PHOTO_BASE_URL}${url}`;

  const galleryImages = sortedPhotos.map(photo => normalizePhotoUrl(photo.PhotoURL));
    
    // const photoTypes = propertyPhotos.map(photo => photo.PhotoType || "PG");
    // const rawPhotos = propertyPhotos.map(photo => ({
    //   PhotoID: photo.PhotoID,
    //   PhotoType: photo.PhotoType,
    //   PhotoTitle: photo.PhotoTitle,
    //   PhotoURL: photo.PhotoURL,
    //   DisplayOrder: photo.DisplayOrder,
    //   IsPrimary: photo.IsPrimary,
    //   Status: photo.Status
    // }));

     const photoTypes = sortedPhotos.map(photo => photo.PhotoType || "PG");
  const rawPhotos = sortedPhotos.map(photo => ({
    PhotoID: photo.PhotoID,
    PhotoType: photo.PhotoType,
    PhotoTitle: photo.PhotoTitle,
    PhotoURL: normalizePhotoUrl(photo.PhotoURL),
    DisplayOrder: photo.DisplayOrder,
    IsPrimary: photo.IsPrimary,
    Status: photo.Status
  }));

  // Find photo for room type
  // const photo = sortedPhotos.find(p => p.PhotoType === photoType);

    const pgRent = parseInt(pgDetails.PGRent) || 0;

    return {
      id: pgid,
      PGID: pgid,
      name: pgDetails.PGName,
      gender: pgDetails.PGType === "Boys" ? "Boys" : pgDetails.PGType === "Girls" ? "Girls" : "Co-ed",
      location: pgDetails.PGLocality,
      lat: finalLat,
      lng: finalLng,
      about: aboutUsInfo || "No description available.",
      isVerified: pgDetails.IsVerified === true,
      occupancy: {
        filled: (occupancyData.SingleOccupied || 0) + (occupancyData.DoubleleOccupied || 0) + (occupancyData.TripleOccupied || 0),
        total: (occupancyData.SINGLE ? parseInt(occupancyData.SINGLE.split('/')[1]) : 0) +
          (occupancyData.DOUBLE ? parseInt(occupancyData.DOUBLE.split('/')[1]) : 0) +
          (occupancyData.TRIPLE ? parseInt(occupancyData.TRIPLE.split('/')[1]) : 0)
      },
      rentingTerms: [
        { icon: "₹", label: "Rent Range", value: pgRent ? `₹${pgRent.toLocaleString()}` : "Contact" },
        { icon: "🔒", label: "Security Deposit", value: pgAdditionalInfo.securitydeposit ? `₹${parseFloat(pgAdditionalInfo.securitydeposit).toLocaleString()}` : "Contact" },
        { icon: "📅", label: "Lock-in Period", value: pgAdditionalInfo.LockinPeriod || "Flexible" },
        { icon: "📢", label: "Notice Period", value: pgAdditionalInfo.NoticePeriod || "30 days" },
        { icon: "⚡", label: "Electricity", value: pgAdditionalInfo.electricityunit ? `₹${pgAdditionalInfo.electricityunit}/unit` : "As per usage" },
      ],
      amenities: amenities,
      // packages: validPackages.length > 0 ? validPackages : [
      //   {
      //     type: "Single Sharing",
      //     price: pgRent || 10000,
      //     available: 0,
      //     total: 0,
      //     roomAmenities: []
      //   }
      // ],
      packages: validPackages,
      rules: propertyRules,
      pgRent: parseInt(pgDetails.PGRent) || 0,
      owner: {
        name: ownerInfo?.Rules || "Property Owner",
        since: rules.find(r => r.RulesType === "OWNER" && r.Sequence === 2)?.Rules || "2024",
        hosted: rules.find(r => r.RulesType === "OWNER" && r.Sequence === 3)?.Rules || "0",
        bio: rules.find(r => r.RulesType === "OWNER" && r.Sequence === 3)?.Rules || "Experienced property owner dedicated to providing comfortable accommodation."
      },
      galleryImages: galleryImages.length > 0 ? galleryImages : [
        "https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=900&h=600&fit=crop&auto=format",
        "https://images.unsplash.com/photo-1618773928121-c32242e63f39?w=600&h=400&fit=crop&auto=format"
      ],
      photoTypes: photoTypes,
      rawPhotos: rawPhotos,
    };
  };

  // Overpass mirrors — raced in PARALLEL (not sequential). openstreetmap.ru
  // is dropped: it was returning net::ERR_CONNECTION_TIMED_OUT, meaning the
  // TCP connection never completes at all (network-level block, likely
  // ISP/firewall — very common for .ru domains). Trying it sequentially
  // before other mirrors meant burning a full timeout on a domain that can
  // never succeed, before even attempting a mirror that could.
  const OVERPASS_MIRRORS = [
    "https://overpass-api.de/api/interpreter",
    "https://overpass.kumi.systems/api/interpreter",
  ];

  // Fires all mirrors at once and returns whichever responds successfully
  // FIRST (Promise.any) — a dead/slow mirror no longer delays a working one.
  // Total worst-case wait is now `timeoutMs` (one timeout), not
  // `timeoutMs * mirrors.length` like the old sequential version.
  const fetchOverpass = async (query, { timeoutMs = 15000 } = {}) => {
    const attempts = OVERPASS_MIRRORS.map((url) => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

      return fetch(url, {
        method: "POST",
        body: query,
        signal: controller.signal,
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      })
        .then((response) => {
          clearTimeout(timeoutId);
          if (!response.ok) {
            throw new Error(`Overpass ${url} returned HTTP ${response.status}`);
          }
          return response.json();
        })
        .catch((err) => {
          clearTimeout(timeoutId);
          throw err;
        });
    });

    try {
      // Resolves as soon as ANY mirror succeeds; only rejects if ALL do
      return await Promise.any(attempts);
    } catch (aggregateError) {
      throw new Error("All Overpass mirrors failed");
    }
  };

// Non-blocking nearby places fetch with retry logic - IMPROVED
const fetchNearbyPlacesNonBlocking = useCallback(async (lat, lng, locality) => {
  // Immediately show static fallback data
  const staticData = getStaticNearbyData(locality);
  
  // Only update if component is still mounted
  if (isMountedRef.current) {
    setNearbyPlaces(staticData);
    setNearbyLoading(false);
  }

  // Track which categories have data
  let categoryStatus = {
    Utilities: { loaded: false, data: [], markers: [] },
    Transit: { loaded: false, data: [], markers: [] },
    Food: { loaded: false, data: [], markers: [] },
    Shopping: { loaded: false, data: [], markers: [] }
  };

  // Try to fetch real data in background with retries
  const fetchRealData = async () => {
    // Check if component is still mounted
    if (!isMountedRef.current) {
      return;
    }

    try {
      // Define queries (same as before)
      const transitQuery = `
        [out:json][timeout:12];
        (
          node["railway"="station"]["station"="subway"](around:2500,${lat},${lng});
          node["railway"="subway_entrance"](around:2500,${lat},${lng});
          node["railway"="station"]["subway"="yes"](around:2500,${lat},${lng});
          node["station"="subway"](around:2500,${lat},${lng});
          node["railway"="station"](around:2500,${lat},${lng});
          node["highway"="bus_stop"](around:2500,${lat},${lng});
          node["public_transport"="platform"](around:2500,${lat},${lng});
          node["bus"="yes"](around:2500,${lat},${lng});
          node["amenity"="bus_station"](around:2500,${lat},${lng});
          node["railway"="tram_stop"](around:2500,${lat},${lng});
        );
        out body;
      `;

      const foodQuery = `
        [out:json][timeout:12];
        (
          node["amenity"~"restaurant|cafe|fast_food|food_court"](around:2500,${lat},${lng});
        );
        out body;
      `;

      const shoppingQuery = `
        [out:json][timeout:12];
        (
          node["shop"~"supermarket|mall|department_store|convenience"](around:2500,${lat},${lng});
        );
        out body;
      `;

      const utilitiesQuery = `
        [out:json][timeout:12];
        (
          node["amenity"~"hospital|clinic|pharmacy|bank|atm"](around:2500,${lat},${lng});
        );
        out body;
      `;

      const categories = [
        { name: 'Transit', query: transitQuery, displayName: 'Transit' },
        { name: 'Food', query: foodQuery, displayName: 'Food' },
        { name: 'Shopping', query: shoppingQuery, displayName: 'Shopping' },
        { name: 'Utilities', query: utilitiesQuery, displayName: 'Utilities' }
      ];

      // Retry function for a single category with exponential backoff
      const fetchWithRetry = async (categoryName, query, maxRetries = 5) => {
        let lastError = null;
        const baseDelay = 5000;
        
        for (let attempt = 1; attempt <= maxRetries; attempt++) {
          // Check if component is still mounted
          if (!isMountedRef.current) {
            return { category: categoryName, success: false, cancelled: true };
          }

          try {
            const result = await fetchOverpass(query, { timeoutMs: 15000 });
            
            // Check mount status again after await
            if (!isMountedRef.current) {
              return { category: categoryName, success: false, cancelled: true };
            }

            // Process and update results for this category
            const processed = processCategoryData(categoryName, result.elements || []);
            if (processed.hasData) {
              categoryStatus[categoryName].loaded = true;
              categoryStatus[categoryName].data = processed.data;
              categoryStatus[categoryName].markers = processed.markers;
              
              // Only update UI if still mounted
              if (isMountedRef.current) {
                updateUI();
              }
              
              return { category: categoryName, success: true };
            } else {
              categoryStatus[categoryName].loaded = true;
              categoryStatus[categoryName].data = [];
              categoryStatus[categoryName].markers = [];
              
              if (isMountedRef.current) {
                updateUI();
              }
              return { category: categoryName, success: true, empty: true };
            }
          } catch (error) {
            lastError = error;
            
            // Check mount status
            if (!isMountedRef.current) {
              return { category: categoryName, success: false, cancelled: true };
            }
            
            const isRateLimit = error.message?.includes('429') || error.message?.includes('rate limit');
            
            if (attempt < maxRetries) {
              const waitTime = isRateLimit 
                ? baseDelay * Math.pow(2, attempt)
                : baseDelay * Math.pow(1.5, attempt - 1);
              
              await new Promise(resolve => setTimeout(resolve, waitTime));
            } else {
              categoryStatus[categoryName].loaded = true;
              categoryStatus[categoryName].data = [];
              categoryStatus[categoryName].markers = [];
              
              if (isMountedRef.current) {
                updateUI();
              }
            }
          }
        }
        
        return { category: categoryName, success: false, error: lastError };
      };

      // Process a category's data
      const processCategoryData = (category, elements) => {
        const result = { data: [], markers: [] };
        const MAX_RESULTS = {
          Transit: 8,
          Food: 8,
          Shopping: 8,
          Utilities: 8
        };

        if (!elements || elements.length === 0) {
          return { hasData: false, data: [], markers: [] };
        }

        elements.forEach((el) => {
          const t = el.tags || {};
          let name = t.name || t["name:en"];

          // Skip places OSM has no real name for instead of showing a fake
          // "Unknown Place" placeholder — this was also eating one of the
          // MAX_RESULTS slots and crowding out genuinely-named places.
          if (!name || !isValidPlaceName(name)) return;
          
          name = name.trim();
          const distance = `${calculateDistance(lat, lng, el.lat, el.lon).toFixed(1)} km`;
          
          let transitType = "";
          let displayName = name;
          
          if (category === "Transit") {
            name = name.replace(/\s*(Bus Stop|Bus Stand|Metro Station|Railway Station|Station)\s*$/i, '');
            name = name.trim();
            
            const isMetro = name.toLowerCase().includes("metro") ||
              name.toLowerCase().includes("subway") ||
              t.railway === "station" && (t.station === "subway" || t.subway === "yes") ||
              t.railway === "subway_entrance" ||
              name.toLowerCase().includes("rohini east") ||
              name.toLowerCase().includes("rohini west") ||
              name.toLowerCase().includes("pitampura");
            
            const isBus = name.toLowerCase().includes("bus") ||
              t.highway === "bus_stop" ||
              t.amenity === "bus_station" ||
              t.bus === "yes" ||
              name.toLowerCase().includes("depot") ||
              name.toLowerCase().includes("chowk");
            
            if (isMetro && !isBus) {
              transitType = "metro";
              displayName = name;
            } else if (isBus) {
              transitType = "bus";
              displayName = name;
            } else {
              transitType = "transit";
              displayName = name;
            }
          }
          
          const isDuplicate = result.data.some(existing => 
            existing.name.toLowerCase() === displayName.toLowerCase() &&
            Math.abs(parseFloat(existing.distance) - parseFloat(distance)) < 0.2
          );
          
          if (isDuplicate) return;
          if (result.data.length >= MAX_RESULTS[category]) return;
          
          result.data.push({
            name: displayName,
            originalName: name,
            distance: distance,
            type: transitType
          });
          
          result.markers.push({
            name: displayName,
            originalName: name,
            lat: el.lat,
            lng: el.lon,
            distance: distance,
            type: transitType,
            tags: t
          });
        });
        
        result.data.sort((a, b) => {
          if (category === "Transit") {
            const aIsMetro = a.type === "metro";
            const bIsMetro = b.type === "metro";
            if (aIsMetro && !bIsMetro) return -1;
            if (!aIsMetro && bIsMetro) return 1;
          }
          return parseFloat(a.distance) - parseFloat(b.distance);
        });
        
        result.markers.sort((a, b) => {
          if (category === "Transit") {
            const aIsMetro = a.type === "metro";
            const bIsMetro = b.type === "metro";
            if (aIsMetro && !bIsMetro) return -1;
            if (!aIsMetro && bIsMetro) return 1;
          }
          return parseFloat(a.distance) - parseFloat(b.distance);
        });
        
        return { 
          hasData: result.data.length > 0, 
          data: result.data, 
          markers: result.markers 
        };
      };

      // Update UI with current data
      const updateUI = () => {
        // Check mount status
        if (!isMountedRef.current) return;
        
        const result = { Utilities: [], Transit: [], Food: [], Shopping: [] };
        const markers = { Utilities: [], Transit: [], Food: [], Shopping: [] };
        
        Object.keys(categoryStatus).forEach(cat => {
          if (categoryStatus[cat].loaded) {
            result[cat] = categoryStatus[cat].data;
            markers[cat] = categoryStatus[cat].markers;
          }
        });
        
        let hasAnyData = false;
        Object.values(result).forEach(arr => { if (arr.length > 0) hasAnyData = true; });
        
        if (hasAnyData) {
          setNearbyPlaces(result);
          setNearbyMarkers(markers);
        }
      };

      // Helper functions
      const isValidPlaceName = (name) => {
        if (!name) return false;
        const excludeKeywords = [
          'flat', 'apartment', 'residential', 'housing', 'colony',
          'block', 'sector', 'extension', 'enclave', 'vihar',
          'garden', 'park', 'society', 'building', 'tower',
          'dda', 'lig', 'mig', 'janta', 'nagar', 'kali mata',
          'mandir', 'temple', 'church', 'mosque', 'gurudwara',
          'school', 'college', 'university', 'institute'
        ];
        const lowerName = name.toLowerCase();
        for (const keyword of excludeKeywords) {
          if (lowerName.includes(keyword)) return false;
        }
        return true;
      };

      const calculateDistance = (lat1, lng1, lat2, lng2) => {
        const R = 6371;
        const dLat = ((lat2 - lat1) * Math.PI) / 180;
        const dLng = ((lng2 - lng1) * Math.PI) / 180;
        const a = Math.sin(dLat / 2) ** 2 +
          Math.cos((lat1 * Math.PI) / 180) *
          Math.cos((lat2 * Math.PI) / 180) *
          Math.sin(dLng / 2) ** 2;
        return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      };

      // Fetch all categories with retries in parallel
      const fetchPromises = categories.map(cat => 
        fetchWithRetry(cat.name, cat.query)
      );

      await Promise.allSettled(fetchPromises);
      
      if (isMountedRef.current) {
        updateUI();
      }

    } catch (error) {
      // Background fetch failed entirely — static fallback data (already
      // shown) stays in place, nothing further to do.
    }
  };

  // Start background fetch
  fetchRealData();
}, []);

// Add cleanup in useEffect
useEffect(() => {
  // Set mounted flag to true
  isMountedRef.current = true;
  
  return () => {
    // Set mounted flag to false when component unmounts
    isMountedRef.current = false;
  };
}, []);

  const openBooking = (packageType = "") => {
    setBookingPackage(packageType);
    setBookingOpen(true);
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    setActiveTab("About");
    setShowFullAbout(false);
    setShowFullOwner(false);
    setSaved(false);
    setNearbyPlaces(null);
    setNearbyMarkers({});
  }, [pgName]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            const s = e.target.getAttribute("data-section");
            if (s) setActiveTab(s);
          }
        });
      },
      { rootMargin: "-30% 0px -60% 0px", threshold: 0 }
    );

    Object.values(sectionRefs.current).forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [pgData]);

  // Add this useEffect in your PGDetail component (with other useEffects)
  useEffect(() => {
    return () => {
      // Cleanup map instances on unmount
      const mapElement = document.querySelector('.leaflet-map');
      if (mapElement && mapElement._leaflet_id) {
        try {
          const map = mapElement._leaflet_map;
          if (map && typeof map.remove === 'function') {
            map.remove();
          }
        } catch (e) {
          // Ignore cleanup errors
        }
      }
    };
  }, []);

  const scrollToSection = (tab) => {
    const el = sectionRefs.current[tab];
    if (!el) return;
    const offset = 68 + (tabsRef.current?.offsetHeight || 48) + 8;
    window.scrollTo({
      top: el.getBoundingClientRect().top + window.scrollY - offset,
      behavior: "smooth",
    });
    setActiveTab(tab);
  };

  // if (loading) {
  //   return (
  //     <div className="pgdetail-page page">
  //       <div className="container">
  //         <div className="loading-spinner">Loading property details...</div>
  //       </div>
  //     </div>
  //   );
  // }

  if (loading) {
    return (
      <main className="pgdetail-page page">
        <div className="container">
          <div className="loading-spinner" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', width: '100%' }}>
            <img 
              src="/assets/aapkapgLoader.gif" 
              alt="Loading..." 
              style={{ width: 'auto', height: '10vh', marginBottom: '15vh' }}
            />
          </div>
        </div>
      </main>
    );
  }

  if (error || !pgData) {
    return (
      <div className="pgdetail-page page">
        <div className="container">
          <div className="error-message">{error || "Property not found"}</div>
          <button onClick={() => navigate(-1)}>Go Back</button>
        </div>
      </div>
    );
  }

  const genderClass = pgData.gender === "Boys" ? "gender-boys" : pgData.gender === "Girls" ? "gender-girls" : "gender-coed";
  // const minPrice = Math.min(...pgData.packages.map((p) => p.price));
  const validPrices = pgData.packages
    .map((p) => p.price)
    .filter((price) => price != null && !isNaN(price) && price > 0);
  const minPrice = validPrices.length > 0 ? Math.min(...validPrices) : null;

  return (
    <>
      <main className="pgdetail-page page">
        <div className="container">
          {/* Header */}
          <div className="pgdetail-header">
            <button className="pgdetail-back" onClick={() => navigate(-1)}>
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                <path d="M13 4L7 10L13 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
              Back
            </button>
            <div className="pgdetail-title-row">
              <div className="pgdetail-title-left">
                <h1 className="pgdetail-name">{pgData.name}</h1>
                <div className="pgdetail-meta">
                  <span className={`pgdetail-gender-badge ${genderClass}`}>
                    {pgData.gender}
                  </span>
                  <span className="pgdetail-location">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                    {pgData.location}
                  </span>
                  <span className="pgdetail-occupancy">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
                    </svg>
                    {pgData.occupancy.filled}/{pgData.occupancy.total} occupied
                  </span>
                </div>
              </div>
              <div className="pgdetail-title-actions">
                <button
                  className={`pgdetail-save-btn ${saved ? "saved" : ""}`}
                  onClick={() => setSaved((s) => !s)}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill={saved ? "#155DFC" : "none"} stroke={saved ? "#155DFC" : "currentColor"} strokeWidth="2">
                    <path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z" />
                  </svg>
                  {saved ? "Saved" : "Save"}
                </button>
                <button className="pgdetail-share-btn">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="18" cy="5" r="3" />
                    <circle cx="6" cy="12" r="3" />
                    <circle cx="18" cy="19" r="3" />
                    <path d="M8.59 13.51l6.83 3.98M15.41 6.51l-6.82 3.98" />
                  </svg>
                  Share
                </button>
              </div>
            </div>
          </div>

          {/* Gallery */}
          <div className="pgdetail-gallery">
            {pgData.isVerified && <img className="verified-badge" src="/assets/verified-badge.png" alt="" />}

            <div className="gallery-hero"
              //  onClick={() => setLightbox(0)}
              onClick={() => openGallery(pgData.rawPhotos?.[0]?.PhotoID, "lightbox")}
            >
              <MediaItem src={pgData.galleryImages[0]} alt="PG Main" showPlayBadge />
            </div>
            <div className="gallery-thumbs">
              {pgData.galleryImages.slice(1, 5).map((img, i) => {
                // Determine if this is the last visible thumbnail in the current viewport
                const isMobile = window.innerWidth <= 768;
                const isLastVisible = isMobile ? i === 1 : i === 3;
                const showMore = isLastVisible && pgData.galleryImages.length > (isMobile ? 3 : 4);
                const extraCount = pgData.galleryImages.length - (isMobile ? 3 : 4);

                return (
                  <div
                    key={i}
                    className={`gallery-thumb ${showMore ? "gallery-thumb-more" : ""}`}
                    onClick={() => {
                      const photoId = pgData.rawPhotos?.[i + 1]?.PhotoID;
                      showMore ? openGallery(photoId, "grid") : openGallery(photoId, "lightbox");
                    }}
                  >
                    <MediaItem src={img} alt={`Room ${i + 2}`} showPlayBadge={!showMore} />
                    {showMore && (
                      <div className="gallery-more-overlay">
                        <span>+{extraCount} Photos</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Sticky tabs */}
        <div className="pgdetail-tabs-bar" ref={tabsRef}>
          <div className="container">
            <div className="pgdetail-tabs">
              {SECTION_TABS.map((tab) => (
                <button
                  key={tab}
                  className={`pgdetail-tab ${activeTab === tab ? "active" : ""}`}
                  onClick={() => scrollToSection(tab)}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="container">
          <div className="pgdetail-sections">
            {/* About */}
            <section
              className="pgdetail-section pgdetail-about-full"
              data-section="About"
              ref={(el) => (sectionRefs.current["About"] = el)}
            >
              <h2 className="section-title">About Property</h2>
              <div className="about-text">
                <ClampedText text={pgData.about} />
              </div>
            </section>

            {/* Amenities and Location Grid */}
            <div className="pgdetail-mid-grid">
              {/* Amenities */}
              <section className="pgdetail-section pgdetail-mid-left">
                <h2 className="section-title">Amenities</h2>
                {/* <div className="amenity-tabs">
                  {Object.keys(pgData.amenities).map((tab) => (
                    pgData.amenities[tab].length > 0 && (
                      <button
                        key={tab}
                        className={`amenity-tab ${amenityTab === tab ? "active" : ""}`}
                        onClick={() => setAmenityTab(tab)}
                      >
                        {tab}
                      </button>
                    )
                  ))}
                </div> */}
                <div className="amenity-grid">
                  {pgData.amenities[amenityTab]?.map((item, i) => (
                    <div key={i} className="amenity-item">
                      <span className="amenity-check">{getAmenityIcon(item)}</span>
                      {item}
                    </div>
                  ))}
                </div>
              </section>

              {/* Location */}
              <section className="pgdetail-section pgdetail-mid-right">
                <h2 className="section-title">Location</h2>

                <div className="location-split">
                  {/* Left: Map */}
                  <div className="location-map-col">
                    <div className="location-map-wrapper">
                      <MapContainer
                        center={[pgData.lat, pgData.lng]}
                        zoom={15}
                        className="leaflet-map"
                        scrollWheelZoom={true}
                        zoomControl={false}
                        whenReady={() => setMapReady(true)}
                        touchZoom={true}
                        doubleClickZoom={true}
                        dragging={true}
                        tap={true}
                      >
                        <TileLayer
                          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        <ZoomControl position="bottomright" />
                        <ScaleControl imperial={false} position="bottomleft" />

                        <Circle
                          center={[pgData.lat, pgData.lng]}
                          radius={2500}
                          pathOptions={{
                            color: "#155DFC",
                            fillColor: "#155DFC",
                            fillOpacity: 0.05,
                            weight: 1.5,
                            dashArray: "5, 5"
                          }}
                        />

                        <Marker
                          position={[pgData.lat, pgData.lng]}
                          icon={createPGMarkerIcon()}
                        >
                          <Popup className="pg-popup" closeButton={false}>
                            <div className="custom-popup">
                              <img src={getFirstImageUrl(pgData.galleryImages)} alt={pgData.name} className="popup-image" />
                              <div className="popup-content">
                                <h4>{pgData.name}</h4>
                                <p className="popup-location">{pgData.location}</p>
                                {minPrice != null ? (
                                  <div className="popup-price">
                                    <span className="price">₹{minPrice.toLocaleString()}</span>
                                    <span className="unit">/month</span>
                                  </div>
                                ) : (
                                  <div className="popup-price">
                                    <span className="price" style={{ fontSize: "12px" }}>Contact owner for rent</span>
                                  </div>
                                )}
                                <button className="popup-btn" onClick={() => openBooking()}>
                                  View Details
                                </button>
                              </div>
                            </div>
                          </Popup>
                        </Marker>

                        <NearbyMarkers
                          markers={nearbyMarkers[nearbyTab] || []}
                          category={nearbyTab}
                        />

                        <MapController
                          markers={nearbyMarkers[nearbyTab] || []}
                          pgPosition={{ lat: pgData.lat, lng: pgData.lng }}
                        />
                      </MapContainer>

                      <MapLegend />
                      <a
                        href={`https://www.google.com/maps?q=${pgData.lat},${pgData.lng}`}
                        target="_blank"
                        rel="noreferrer"
                        className="map-open-btn"
                      >
                        Open in Google Maps ↗
                      </a>
                    </div>
                  </div>

                  {/* Right: Nearby Places */}
                  <div className="location-nearby-col">
                    <h3 className="nearby-title">Nearby Places</h3>
                    <div className="nearby-tabs">
                      {NEARBY_TABS.map((tab) => (
                        <button
                          key={tab}
                          className={`nearby-tab ${nearbyTab === tab ? "active" : ""}`}
                          onClick={() => setNearbyTab(tab)}
                        >
                          {tab}
                        </button>
                      ))}
                    </div>
                    <div className="nearby-grid two-columns">
                      {nearbyLoading ? (
                        <div className="nearby-loading full-width">
                          <div className="nearby-spinner"></div>
                          <span>Discovering nearby {nearbyTab.toLowerCase()} places...</span>
                        </div>
                      ) : (nearbyPlaces?.[nearbyTab] || []).length > 0 ? (
                        (nearbyPlaces?.[nearbyTab] || []).map((place, i) => (
                          <div key={i} className="nearby-item">
                            <span className="nearby-name" title={place.name}>
                              {nearbyTab === "Transit" && place.type === "metro" && "🚇 "}
                              {nearbyTab === "Transit" && place.type === "bus" && "🚌 "}
                              {nearbyTab === "Transit" && place.type === "transit" && "🚏 "}
                              {nearbyTab === "Food" && "🍔 "}
                              {nearbyTab === "Shopping" && "🛒 "}
                              {nearbyTab === "Utilities" && "🏥 "}
                              {place.name}
                            </span>
                            <div className="nearby-distance-info">
                              <span className="nearby-distance">{place.distance}</span>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="nearby-empty full-width">
                          <span className="nearby-empty-icon">🔍</span>
                          <span className="nearby-empty-title">Finding {nearbyTab.toLowerCase()} places for you</span>
                          <span className="nearby-empty-hint">We're scanning the area. If nothing shows up, try another category.</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </section>
            </div>

            {/* Renting Terms */}
            <section
              className="pgdetail-section"
              data-section="Renting Terms"
              ref={(el) => (sectionRefs.current["Renting Terms"] = el)}
            >
              <h2 className="section-title">Renting Terms</h2>
              <div className="renting-terms-grid">
                {pgData.rentingTerms.map((term, i) => (
                  <div key={i} className="renting-term-card">
                    <span className="renting-term-icon">{term.icon}</span>
                    <div className="renting-term-divider">
                      <p className="renting-term-label">{term.label}</p>
                      <p className="renting-term-value">{term.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Packages */}
            <section
              className="pgdetail-section"
              data-section="Packages"
              ref={(el) => (sectionRefs.current["Packages"] = el)}
            >
              <h2 className="section-title">Rent Packages</h2>
              <div className={`packages-grid ${pgData.packages.length === 5 ? "packages-grid-5" : ""}`}>
                {pgData.packages.map((pkg, i) => (
                  <PackageCard
                    key={i}
                    pkg={pkg}
                    onBook={openBooking}
                    onPhotoClick={handlePackagePhotoClick}
                  />
                ))}
              </div>
            </section>

            {/* Rules */}
            <section
              className="pgdetail-section"
              data-section="Rules"
              ref={(el) => (sectionRefs.current["Rules"] = el)}
            >
              <h2 className="section-title">Property Rules</h2>
              <div className="rules-list">
                {pgData.rules.map((rule, i) => (
                  <div key={i} className="rule-item">
                    <span className="rule-icon">✓</span>
                    <p>{rule}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Owner */}
            <section className="pgdetail-section">
              <h2 className="section-title">Property Owner</h2>
              <div className="owner-card">
                <div className="owner-avatar">{pgData.owner.name.charAt(0)}</div>
                <div className="owner-info">
                  <h3 className="owner-name">{pgData.owner.name}</h3>
                  <div className="owner-stats">
                    <span>Hosting since {pgData.owner.since}</span>
                    <span className="owner-stat-dot">·</span>
                    {/* <span>{pgData.owner.hosted} tenants hosted</span> */}
                  </div>
                  <ClampedText text={pgData.owner.bio} className="owner-bio" />
                </div>
              </div>
            </section>

            {/* Similar Properties Nearby */}
            <section className="pgdetail-section pgdetail-similar-section">
              <h2 className="section-title">Similar Properties Nearby</h2>
              {similarLoading ? (
                <div className="similar-loading">
                  <div className="loading-spinner">Loading similar properties...</div>
                </div>
              ) : similarPGs.length > 0 ? (
                <div className="similar-scroll-wrapper">
                  <div className="similar-row">
                    {similarPGs.map((pg) => (
                      <SimilarCard key={pg.PGID} pg={pg} currentPGId={pgData.PGID} />
                    ))}
                  </div>
                </div>
              ) : (
                <div className="similar-placeholder">
                  <p>No similar properties found in this area.</p>
                </div>
              )}
            </section>

{/* PG Nearby Landmarks - Only show if landmarks exist */}
{landmarks.length > 0 && (
  <section className="pgdetail-section pgdetail-pg-near-section">
    <h2 className="section-title">PG Nearby Landmarks</h2>

    <div className="pg-near-list">
      {landmarks.map((item, index) => (
        <React.Fragment key={index}>
          <span className="pg-near-item">{item}</span>
          {index !== landmarks.length - 1 && (
            <span className="pg-near-separator">|</span>
          )}
        </React.Fragment>
      ))}
    </div>
  </section>
)}
          </div>
        </div>

        {/* Sticky CTA - outside the wrapper, fixed at bottom */}
        <div className="pgdetail-sticky-cta">
          <div className="sticky-cta-inner">
            <div className="sticky-cta-price">
              {pgData.pgRent > 0 ? (
                <>
                  <span className="sticky-price-from">Starting from</span>
                  <span className="sticky-price-amount">
                    ₹{pgData.pgRent.toLocaleString("en-IN")}
                  </span>
                  <span className="sticky-price-unit">/month</span>
                </>
              ) : (
                <span className="sticky-price-from" style={{ fontSize: "13px", color: "#000" }}>
                  Contact PG owner for rent
                </span>
              )}
            </div>
            <div className="sticky-cta-actions">
              <button className="cta-btn-primary" onClick={() => openBooking()}>
                Reserve Bed
              </button>
            </div>
          </div>
        </div>
      </main>

      {lightbox !== null && (
        <Lightbox
          images={pgData.galleryImages}
          startIndex={lightbox}
          onClose={() => setLightbox(null)}
        />
      )}

      {bookingOpen && (
        <BookingModal
          pgName={pgData.name}
          packageType={bookingPackage}
          isLoggedIn={isLoggedIn}
          pgId={pgData.PGID}
          onClose={() => setBookingOpen(false)}
        />
      )}

      {photoGalleryOpen && (
        <PhotoGalleryModal
          photos={pgData?.rawPhotos?.length > 0
            ? pgData.rawPhotos.map(photo => ({
              PhotoID: photo.PhotoID,
              PhotoURL: photo.PhotoURL,
              PhotoType: photo.PhotoType || "PG",
              PhotoTitle: photo.PhotoTitle || `Photo ${photo.PhotoID}`,
              DisplayOrder: photo.DisplayOrder,
              IsPrimary: photo.IsPrimary
            }))
            : pgData?.galleryImages?.map((url, index) => ({
              PhotoID: index + 1,
              PhotoURL: url,
              PhotoType: "PG",
              PhotoTitle: `Photo ${index + 1}`,
              DisplayOrder: index + 1,
              IsPrimary: index === 0 ? 1 : 0
            })) || []
          }
          initialTab={selectedPhotoTab} // Pass selected tab
          initialViewMode={photoGalleryViewMode}
          initialSelectedPhotoId={photoGallerySelectedPhotoId}
          onClose={() => {
            setPhotoGalleryOpen(false);
            setSelectedPhotoTab("All"); // Reset tab on close
          }}
        />
      )}
    </>
  );
};

export default PGDetail;